<template>
  <div class="new-upload-wrapper">
    <van-uploader
      ref="upload"
      v-model="vanFileList"
      :multiple="multiple"
      :after-read="afterRead"
      :before-read="beforeRead"
      :before-delete="beforeDelete"
      :max-size="maxSize"
      :max-count="maxCount"
      :accept="acceptTypes"
      :disabled="(isUploading && !isPaused) || isAiChecking || disabled"
      :preview-size="previewSize"
      :result-type="'file'"
      :upload-text="textContent"
      @oversize="onOversize"
      @click-preview="handlePreview"
      @click-upload="onClickUpload"
    />

    <!-- 上传进度条 -->
    <div class="progress" v-if="file && progress !== 100 && progress > 0">
      <el-progress
        :percentage="progress"
        :stroke-width="20"
        :text-inside="true"
        :format="formatProgress"
      />
      <div class="progress-info">
        <span>已传输: {{ formatFileSize(uploadedBytes) }} / {{ formatFileSize(file.size) }}</span>
      </div>
    </div>

    <!-- AI审核状态显示 -->
    <div v-if="is_ok && checkStatus" class="ai-check-status">
      <span :class="['status-indicator', checkStatus]">
        <i :class="getStatusIcon(checkStatus)"></i>
        {{ checkMessage }}
      </span>
    </div>

    <!-- OCR识别结果弹窗（仅手动复制模式） -->
    <van-popup
      v-model="showOcrDialog"
      position="bottom"
      :style="{ maxHeight: '80%' }"
      closeable
      @closed="handleOcrDialogClosed"
      get-container="body"
    >
      <div class="ocr-dialog">
        <!-- 扫描阶段 -->
        <div v-if="ocrStatus === 'scanning'" class="ocr-scanning">
          <div class="ocr-scanning__header">
            <h3>正在识别图片</h3>
            <p class="ocr-scanning__tip">请稍候...</p>
          </div>
          <div class="ocr-scanning__image-container">
            <img :src="ocrPreviewImage" alt="识别中的图片" class="ocr-scanning__image" />
            <div class="ocr-scan-line"></div>
            <div class="ocr-scan-overlay"></div>
          </div>
        </div>

        <!-- 识别完成：仅手动复制模式 -->
        <div v-else-if="ocrStatus === 'completed'" class="ocr-result">
          <div class="ocr-result__header">
            <h3>识别结果</h3>
            <p class="ocr-result__tip">完整识别的文本，可手动复制</p>
          </div>
          <div class="ocr-result__scrollable">
            <div class="ocr-result__image-preview">
              <img :src="ocrPreviewImage" alt="原图片" class="ocr-result__image" @click="previewOcrImage" />
            </div>
            <div class="ocr-manual-text">
              <div class="ocr-manual-text__content" @copy="handleManualCopy">{{ ocrRawText }}</div>
              <p class="ocr-manual-hint">以上为原始识别结果，支持手动选中并复制</p>
              <van-button
                type="primary"
                size="small"
                plain
                @click="copyOcrText"
                class="ocr-copy-btn"
                icon="copy-o"
              >
                复制全部
              </van-button>
            </div>
          </div>
          <div class="ocr-result__footer">
            <van-button block type="default" @click="showOcrDialog = false">关闭</van-button>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 文件预览弹窗（非图片文件，usePopupPreview=true 时使用） -->
    <van-popup
      v-model="showFilePreviewPopup"
      position="bottom"
      :style="{ width: '100%', height: '100%' }"
      get-container="body"
      @closed="handleFilePreviewClosed"
    >
      <div class="file-preview-popup">
        <div class="file-preview-popup__header">
          <span class="file-preview-popup__title">{{ filePreviewName || '文件预览' }}</span>
          <van-icon name="cross" class="file-preview-popup__close" @click="closeFilePreview" />
        </div>

        <!-- 非图片文件：iframe 尝试预览 + 底部操作栏兜底 -->
        <div class="file-preview-popup__body">
          <div v-if="filePreviewLoading" class="file-preview-popup__loading">
            <van-loading size="32px" color="#1989fa">正在加载文件…</van-loading>
          </div>
          <!-- 视频类型：用原生 <video> 直接播放本地 blob URL / 远端 URL，
               避免真机上 iframe 加载 tus 直链无法播放 -->
          <video
            v-if="filePreviewIframeVisible && filePreviewUrl && filePreviewIsVideo"
            :src="filePreviewUrl"
            controls
            playsinline
            webkit-playsinline
            preload="metadata"
            class="file-preview-video"
            @loadedmetadata="filePreviewLoading = false"
            @error="filePreviewLoading = false; filePreviewLoadError = true"
          ></video>
          <iframe
            v-else-if="filePreviewIframeVisible && filePreviewUrl"
            ref="filePreviewIframe"
            :src="filePreviewUrl"
            frameborder="0"
            allowfullscreen
            class="file-preview-iframe"
            @load="filePreviewLoading = false"
          ></iframe>
          <!-- iframe 超时兜底：无法内联渲染时显示文件信息 -->
          <div v-if="filePreviewLoadError" class="file-preview-popup__fallback-card">
            <div class="file-preview-popup__file-icon">
              <van-icon :name="filePreviewIcon" size="48" color="#1989fa" />
            </div>
            <div class="file-preview-popup__file-name">{{ filePreviewName }}</div>
            <div class="file-preview-popup__file-tip">该类型文件无法在此页面内预览，请使用下方按钮下载查看</div>
          </div>
        </div>
        <!-- 底部操作栏：始终显示 -->
        <div class="file-preview-popup__footer">
          <a :href="filePreviewUrl" :download="filePreviewName" class="file-preview-popup__btn file-preview-popup__btn--primary">
            下载文件
          </a>
          <a :href="filePreviewUrl" target="_blank" rel="noopener" class="file-preview-popup__btn file-preview-popup__btn--default">
            在浏览器中打开
          </a>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import axios from 'axios';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { Uploader as VanUploader, Toast, ImagePreview, Popup, Button, Icon } from 'vant';
import { previewFileInPopup } from './filePreviewPopup';
import {
  showUploadBlockMask,
  hideUploadBlockMask,
  setUploadBlockMaskText,
  forceClearUploadBlockMask,
} from './uploadBlockMask';
import 'vant/lib/uploader/index.less';
import { callAiAgentPhotoCheck } from '@/api/person/personApi';
import { ocrImage } from '@/api/agent/agentApi';
import { compressMedia, willTransform } from '@/utils/mediaCompressor';
import { replaceRelayCosfile } from '@/utils/urlTransform';
import store from '@/store';

export default {
  name: 'NewUpload',
  components: {
    [VanUploader.name]: VanUploader,
    [Popup.name]: Popup,
    [Button.name]: Button,
    [Icon.name]: Icon,
  },
  data() {
    return {
      file: null,
      vanFileList: [], // vant uploader 的 fileList 格式: [{url, status, message, name, ...}]
      uploadHeaders: { SourceSystem: 'wechatWeb' },
      uploadUrl: null,
      isUploading: false,
      isPaused: false,
      progress: 0,
      uploadedBytes: 0,
      abortController: null,
      statusMessage: '',
      tusEndpoint: '',
      uploadLink: null,
      successData: null,
      fileUplaod: null,
      emitData: [],
      // ===== 多文件上传队列 =====
      uploadQueue: [],          // 待上传文件队列（vant fileItem）
      isProcessingQueue: false, // 队列是否正在处理中
      // AI审核相关状态
      checkStatus: '', // '', 'checking', 'success', 'error'
      checkMessage: '',
      isAiChecking: false,
      uploadCompleteTimer: null,
      // Formily 相关
      fieldInstance: null,
      // 全局loading实例（isOpenUpload配置开关）
      uploadLoadingInstance: null,
      // OCR 文字提取相关
      showOcrDialog: false,
      ocrStatus: 'scanning', // scanning | completed
      ocrRawText: '',
      ocrPreviewImage: '',
      // 微信/企微 SDK 配置相关状态（仅在 newChooseImage 不为 'none' 且处于微信环境时使用）
      wxConfigured: false,
      agentConfigured: false,
      configPromise: null,
      // 用户在选择对话框中选择「使用浏览器拍照/上传」时置 true，使后续点击事件透传给 vant 原生 input
      shouldTriggerNativeUpload: false,
      // 文件弹窗预览相关状态
      showFilePreviewPopup: false,
      filePreviewUrl: '',
      filePreviewName: '',
      filePreviewLoading: true,
      filePreviewLoadError: false,
      filePreviewIcon: 'description',
      filePreviewIframeVisible: false,
      filePreviewIsVideo: false,
    };
  },
  props: {
    fileList: Array,
    is_ok: String,
    ai_agent_data: String,
    capture: String,
    // Formily 集成相关 props
    errorAdaptorProp: Function,
    onPreviewProp: Function,
    onRemoveProp: Function,
    // 外部传入的 before-read 自定义校验（通过 $props 响应器注入）
    // 会与组件内部的大小校验链式执行：内部校验通过后再调用外部方法
    beforeReadProp: {
      type: Function,
      default: null,
    },
    // 外部传入的 before-upload 自定义校验（通过 $props 响应器注入）
    beforeUploadProp: {
      type: Function,
      default: null,
    },
    // 上传模式相关 props
    listType: {
      type: String,
      default: 'picture-card', // 'text' | 'picture' | 'picture-card'
    },
    drag: {
      type: Boolean,
      default: false,
    },
    textContent: {
      type: String,
      default: '',
    },
    // 是否仅允许图片类型上传
    photoAlbumDisabled: {
      type: Boolean,
      default: false,
    },
    // 企微"从会话选择文件"的文件类型（chooseMessageFile 的 type 参数，直接透传使用）
    // 默认 'file'，与原来的硬编码行为完全一致（type='file' 时会话中只显示非图片/视频文件）；
    // 传什么值就原样用作企微的 type，不做任何映射
    fileType: {
      type: String,
      default: 'file',
    },
    // 文件大小限制（单位：字节），默认约300MB，与 Upload.js 保持一致
    maxSize: {
      type: Number,
      default: 300000 * 1024,
    },
    // 文件大小上限（单位：MB），由表单设计器配置，未配置(<=0)时不限制
    // 微信/企微选图链路绕过了 Vant 的 beforeRead，需单独据此校验
    maxFileSize: {
      type: Number,
      default: 0,
    },
    // 预览图片尺寸
    previewSize: {
      type: [Number, String],
      default: '80px',
    },
    // 最大上传数量
    maxCount: {
      type: [Number, String],
      default: undefined,
    },
    // 是否禁用上传组件（支持 Formily 字段禁用状态）
    disabled: {
      type: Boolean,
      default: false,
    },
    // ========== 微信/企微选图选文件相关 props ==========
    // 是否启用微信/企微原生选图选文件能力（仅在微信/企微 UA 环境下生效）
    // 支持四个选项：
    // 'none' - 只显示浏览器原生上传（默认）
    // 'both' - 显示所有三个选项（浏览器上传 + 微信拍照/选图 + 从会话选择文件）
    // 'chat' - 显示两个选项（浏览器上传 + 从会话选择文件）
    // 'wework' - 显示两个选项（浏览器上传 + 微信拍照/选图）
    // 注意：浏览器上传选项始终存在，企微相关选项根据配置显示
    // 兼容处理：如果传入的是布尔值，true 相当于 'both'，false 相当于 'none'
    newChooseImage: {
      type: [String, Boolean],
      default: 'none',
    },
    // FormData 字段名，对应 Upload.js 中 field.value.componentProps?.name
    uploadName: {
      type: String,
      default: '',
    },
    // 微信/企微方式上传时使用的 HTTP 接口地址（与 Upload.js 中 action 一致）
    // 仅用于微信/企微选图选文件后的直传（cosfile）。常规 vant afterRead 走 TUS，不使用此地址。
    uploadAction: {
      type: String,
      default: '',
    },
    // 微信/企微方式上传时附加的请求头
    uploadHeaders: {
      type: Object,
      default: () => ({}),
    },
    // 微信/企微方式上传时附加的表单字段
    uploadData: {
      type: Object,
      default: () => ({}),
    },
    expiration: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    // 是否使用弹窗方式预览非图片文件（而非 window.open 新窗口）
    // true: 用 van-popup + iframe 弹窗预览，关闭弹窗返回当前页面
    // false: 用 window.open 新窗口打开（旧行为）
    usePopupPreview: {
      type: Boolean,
      default: false,
    },
    // ========== 媒体压缩相关 props（表单设计器 componentProps） ==========
    // 参数 1：压缩结果的文件格式
    //   'none'  → 不压缩，视频原样上传（默认）
    //   'auto'  → 视频保持原格式压缩体积（走 ffmpeg.wasm 视频转视频）
    //   'video' → 同 'auto'，语义更明确
    //   'gif'   → 视频转 GIF（走 gif.js）
    // 说明：仅对视频文件生效，图片文件始终原样直传。转换失败会降级为上传原文件并 Toast 提示。
    compressFormat: {
      type: String,
      default: 'none',
      validator: (v) => ['auto', 'gif', 'video', 'none'].includes(v),
    },
    // 上传时全屏遮罩开关（表单设计器 componentProps）
    // true  → 从选完文件（含视频压缩）到全部上传完成期间，覆盖全屏遮罩，
    //         屏蔽一切点击，避免用户在文件未上传完成时点到表单提交按钮导致数据缺失
    // false → 不展示遮罩（默认，保持原有行为）
    // 说明：独立 DOM 实现，不受 Toast 单例被后续 Toast 顶掉的影响
    // 上传时全屏遮罩：开启后上传期间全屏遮罩，禁止点击提交按钮，避免上传未完成时提交
    blockingLoading: {
      type: Boolean,
      default: false,
    },
    // 参数 2：压缩比例（0~1，越大越清晰、体积越大）
    //   1     → 视觉无损档
    //   0.6   → 默认，平衡画质与体积
    //   趋近于 0 → 极限压缩，画面明显退化
    compressRatio: {
      type: Number,
      default: 0.6,
      validator: (v) => typeof v === 'number' && v > 0 && v <= 1,
    },
  },
  computed: {
    ...mapGetters(['getConfigItem']),
    // ========== 媒体压缩功能总开关（七彩石） ==========
    // 配置 key：enable_media_compress，值为 '1' 时启用，其余任何值/未配置/读取异常 → 关闭
    // 关闭时 effectiveCompressFormat 恒为 'none'，即使表单传了 compressFormat 也不生效，
    // 整个压缩链路（ffmpeg.wasm / gif.js / MediaRecorder / Loading / 文件名替换）完全不执行，
    // 等价于此功能不存在。用于大改动上线后的风险控制：出问题可在七彩石一键关闭，无需改代码发版。
    enableMediaCompress() {
      try {
        return this.getConfigItem('enable_media_compress') === '1';
      } catch (e) {
        // 任何异常（配置未拉取 / getter 缺失）都按关闭处理，零风险兜底
        return false;
      }
    },
    // 实际生效的压缩格式：开关关闭时强制 'none'
    effectiveCompressFormat() {
      if (!this.enableMediaCompress) return 'none';
      return this.compressFormat || 'none';
    },
    // 根据 photoAlbumDisabled 动态计算 accept 文件类型
    acceptTypes() {
      if (this.photoAlbumDisabled) {
        return 'image/*';
      }
      return '*/*';
    },
    // 获取ProcessDefinitionKey
    processDefinitionKey() {
      try {
        let parent = this.$parent;
        while (parent) {
          if (parent.$options.name === 'FormProvider' || parent.form) {
            const form = parent.form;
            if (form && form.values && form.values.$context) {
              return form.values.$context.ProcessDefinitionKey;
            }
          }
          parent = parent.$parent;
        }
      } catch (e) {
        console.warn('获取ProcessDefinitionKey失败:', e);
      }
      return undefined;
    },
    // PC端上传限制检测
    isUploadPC() {
      const uploadPCLimit = JSON.parse(this.getConfigItem('uploadPCLimit') || '[]');
      const processKey = this.processDefinitionKey;
      return processKey && (uploadPCLimit.includes(processKey) || processKey.startsWith('repair_instance'));
    },
    // 是否处于微信/企微环境
    isWechatEnv() {
      if (typeof window === 'undefined') return false;
      const ua = window.navigator.userAgent.toLowerCase();
      return ua.includes('micromessenger') || ua.includes('wxwork');
    },
    // 规范化 newChooseImage 的值，兼容布尔类型
    normalizedNewChooseImage() {
      const value = this.newChooseImage;
      // 处理 null/undefined
      if (value === null || value === undefined) {
        return 'none';
      }
      // 兼容布尔类型
      if (typeof value === 'boolean') {
        return value ? 'both' : 'none';
      }
      // 确保返回有效值
      if (['none', 'both', 'chat', 'wework'].includes(value)) {
        return value;
      }
      // 无效值返回默认值
      console.warn(`newChooseImage 收到无效值: ${value}，使用默认值 'none'`);
      return 'none';
    },
    // 是否走微信/企微选图选文件流程：新开关 + 微信环境
    shouldUseWechatApi() {
      return this.normalizedNewChooseImage !== 'none' && this.isWechatEnv;
    },
  },
  watch: {
    fileList: {
      handler(newVal) {
        // 仅在初始加载（emitData 为空）时同步 fileList，避免上传成功后 Formily value 更新触发 watch 导致文件重复展示
        if (newVal && this.emitData.length === 0) {
          const List = JSON.parse(JSON.stringify(newVal));
          this.fileUplaod = List;
          // 初始化 van-uploader 的 fileList，格式为 [{url, name, status}]
          this.vanFileList = List.map(item => ({
            url: this.replaceDomain(this.resolveFileUrl(item)),
            name: item.name,
            status: 'done',
            // 标记为图片，使 vant uploader 在 URL 没有图片后缀时也能正确渲染缩略图
            // TUS 上传链接没有图片后缀，vant 的 isImageUrl 无法识别，需要手动标记
            isImage: this.isImageByName(item.name),
            // 保留原始数据用于 emit
            _originData: item,
          }));
          // 同步初始化 emitData，确保删除文件时能正确匹配并只移除目标文件
          // 如果不初始化 emitData，刷新页面后删除操作会 emit 空数组，导致所有图片消失
          this.emitData = List.map((item, index) => {
            const uid = 'init_' + Date.now() + '_' + index;
            // 在 vanFileList 对应项上设置 _emitDataUid，用于 beforeDelete 时匹配
            if (this.vanFileList[index]) {
              this.vanFileList[index]._emitDataUid = uid;
            }
            // 构建 response：优先使用原始 response，否则从 url 构建
            const itemResponse = item.response || (item.url ? {
              FileList: [{
                name: item.name,
                status: 'success',
                url: item.url,
              }],
            } : undefined);
            return {
              name: item.name,
              uid: uid,
              response: itemResponse,
            };
          });
        } else if (newVal && this.emitData.length > 0) {
          // 已上传过文件后，外部可能对 fileList 中的 url 进行了修改（如替换为带水印的链接）
          // 此时需要增量更新 vanFileList 和 emitData 中对应项的 url，而不是整体重建（避免文件重复）
          // 注意：不能按 index 对位更新——vanFileList 中可能存在与外部 fileList 项数不一致的情况
          // （如失败的占位项被移除、AI 审核移除等），按 index 对位会"张冠李戴"，把 A 文件的 url
          // 更新到 B 文件上。改用 _emitDataUid 关联匹配。
          newVal.forEach((item, index) => {
            const newUrl = this.resolveFileUrl(item);
            if (!newUrl) return;
            const replacedNewUrl = this.replaceDomain(newUrl);

            // 通过 emitData[index] 的 uid 关联到 vanFileList 中对应项（_emitDataUid 在上传成功时写入）
            const emitUid = this.emitData[index] && this.emitData[index].uid;
            const matchedVanIdx = emitUid != null
              ? this.vanFileList.findIndex(f => f._emitDataUid === emitUid)
              : -1;

            // 更新 vanFileList 中对应项的 url
            if (matchedVanIdx !== -1) {
              const currentUrl = this.vanFileList[matchedVanIdx].url || '';
              if (currentUrl !== replacedNewUrl) {
                this.$set(this.vanFileList[matchedVanIdx], 'url', replacedNewUrl);
                // vant uploader 缩略图优先使用 content（base64），需清空才能让缩略图改用新 url 渲染
                if (this.vanFileList[matchedVanIdx].content) {
                  this.$set(this.vanFileList[matchedVanIdx], 'content', '');
                }
                console.log(`fileList watch: 检测到第${index}项url变化，已更新vanFileList`, currentUrl, '->', replacedNewUrl);
              }
            }

            // 同步更新 emitData 中对应项的 response url
            if (this.emitData[index] && this.emitData[index].response && this.emitData[index].response.FileList && this.emitData[index].response.FileList[0]) {
              const currentEmitUrl = this.emitData[index].response.FileList[0].url || '';
              if (currentEmitUrl !== newUrl) {
                this.$set(this.emitData[index].response.FileList[0], 'url', newUrl);
                console.log(`fileList watch: 检测到第${index}项url变化，已更新emitData`, currentEmitUrl, '->', newUrl);
              }
            }
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.tusEndpoint = this.getConfigItem('newUploadLink');
  },
  mounted() {
    // 获取 Formily Field 实例
    this.getFormilyField();
    // 设置 Formily decoratorType（与 Upload.js 保持一致）
    this.setFormilyDecoratorType();
  },
  beforeDestroy() {
    // 兜底：组件销毁时强制解除遮罩，
    // 避免上传未完成即切页/销毁导致遮罩残留把页面锁死
    forceClearUploadBlockMask();
  },
  methods: {
    // ========== 域名替换方法 ==========
    resolveFileUrl(item) {
      if (!item) return '';
      const itemUrl = item.url || '';
      if (itemUrl && !itemUrl.startsWith('blob:') && !itemUrl.startsWith('data:')) {
        return itemUrl;
      }
      return (item.response && item.response.FileList && item.response.FileList[0] && item.response.FileList[0].url) || '';
    },

    // 统一把 http:// 升级为 https://（协议相对地址也补齐协议）
    toHttps(url) {
      if (typeof url !== 'string' || !url) {
        return url;
      }
      // http://host/path → https://host/path
      if (url.startsWith('http://')) {
        return `https://${url.slice('http://'.length)}`;
      }
      // 协议相对 //host/path：在 http 页面下会被解析成 http，这里强制 https
      if (url.startsWith('//')) {
        return `https:${url}`;
      }
      return url;
    },

    // TUS 直传域名替换：dcops.idc.tencent.com / dcops.woa.com → otob.dcops.qq.com
    // 仅当域名后紧跟 /file-center/tus/relay/cosfile 时才替换，严格匹配、不使用正则
    replaceTusDomain(url) {
      const PROTOCOLS = ['https://', 'http://'];
      const DOMAINS = ['dcops.idc.tencent.com', 'dcops.woa.com'];
      const TUS_PATH = '/file-center/tus/relay/cosfile';
      const VALID_NEXT_CHARS = ['/', '?', '#'];

      let protocol = '';
      for (let i = 0; i < PROTOCOLS.length; i += 1) {
        if (url.startsWith(PROTOCOLS[i])) {
          protocol = PROTOCOLS[i];
          break;
        }
      }
      if (!protocol) {
        return url;
      }

      const afterProtocol = url.slice(protocol.length);
      let domain = '';
      for (let i = 0; i < DOMAINS.length; i += 1) {
        if (afterProtocol.startsWith(DOMAINS[i])) {
          domain = DOMAINS[i];
          break;
        }
      }
      if (!domain) {
        return url;
      }

      // 域名之后必须紧跟 TUS 路径，否则不动（可排除 dcops.woa.com.xxx.com 之类的伪造后缀）
      const rest = afterProtocol.slice(domain.length);
      if (!rest.startsWith(TUS_PATH)) {
        return url;
      }

      // 路径之后必须是末尾或合法分隔符
      const afterPath = rest.slice(TUS_PATH.length);
      if (afterPath !== '' && VALID_NEXT_CHARS.indexOf(afterPath[0]) === -1) {
        return url;
      }

      return `https://otob.dcops.qq.com${rest}`;
    },

    // 域名替换（仅用于展示缩略图 / 预览 / 下载 / 点击打开，上传链路不调用）：
    // https://dcops.idc.tencent.com 与 https://dcops.woa.com 都换成 https://otob.dcops.qq.com
    replaceDomain(url) {
      let target = this.toHttps(url);
      if (!target || typeof target !== 'string') {
        return target;
      }
      target = this.replaceTusDomain(target);
      // 渲染链接路径补全：/relay/cosfile -> /file-center/relay/cosfile
      return replaceRelayCosfile(target, 'newUpload.replaceDomain');
    },

    // 根据文件名判断是否为图片类型
    isImageByName(name) {
      if (!name) return false;
      return /\.(jpeg|jpg|gif|png|svg|webp|jfif|bmp|dpg)$/i.test(name);
    },

    // 通过 HEAD 请求获取文件大小（previewFile 必填参数）
    async getFileSize(url) {
      try {
        const response = await fetch(url, { method: 'HEAD' });
        const size = response.headers.get('content-length');
        return size ? parseInt(size, 10) : 0;
      } catch (e) {
        console.warn('获取文件大小失败:', e);
        return 0;
      }
    },

    // 使用企业微信原生 previewFile 预览文件（支持所有文件类型）
    async previewFileByWx(url, fileName) {
      // 确保 SDK 已配置
      const configured = await this.ensureWechatConfig();
      if (!configured) return false;

      // previewFile 必填 size，通过 HEAD 请求获取
      const fileSize = await this.getFileSize(url);

      return new Promise((resolve) => {
        const wxApi = typeof ww !== 'undefined' ? ww : wx;
        wxApi.ready(() => {
          wxApi.invoke('previewFile', {
            url: url,
            name: fileName || '',
            size: fileSize,
          }, (res) => {
            if (res && res.err_msg && res.err_msg.indexOf('ok') >= 0) {
              resolve(true);
            } else {
              console.warn('previewFile 调用失败:', res);
              resolve(false);
            }
          });
        });
      });
    },

    // 根据文件名获取文件类型图标
    getFileIcon(name) {
      if (!name) return 'description';
      const ext = name.split('.').pop().toLowerCase();
      const iconMap = {
        pdf: 'description',
        doc: 'description', docx: 'description',
        xls: 'description', xlsx: 'description',
        ppt: 'description', pptx: 'description',
        txt: 'description',
        zip: 'description', rar: 'description', '7z': 'description',
      };
      return iconMap[ext] || 'description';
    },

    // ========== Formily 集成方法 ==========

    // 获取 Formily Field 实例
    getFormilyField() {
      try {
        let parent = this.$parent;
        while (parent) {
          if (parent.field && typeof parent.field === 'object') {
            this.fieldInstance = parent.field;
            console.log('找到 Formily Field 实例:', this.fieldInstance);
            break;
          }
          parent = parent.$parent;
        }
      } catch (e) {
        console.warn('获取 Formily Field 实例失败:', e);
      }
    },

    // 设置 Formily decoratorType（与 Upload.js 保持一致）
    setFormilyDecoratorType() {
      if (this.fieldInstance) {
        try {
          this.fieldInstance.decoratorType = this.fieldInstance.title ? 'FormItem' : null;
        } catch (e) {
          console.warn('设置 Formily decoratorType 失败:', e);
        }
      }
    },

    // 设置 Formily 错误反馈
    setFormilyFeedback(error) {
      if (!this.fieldInstance || !this.fieldInstance.setFeedback) {
        return;
      }

      try {
        let message = '';
        if (error) {
          if (this.errorAdaptorProp && typeof this.errorAdaptorProp === 'function') {
            message = this.errorAdaptorProp(error);
          } else {
            message = error.message || error.toString();
          }
        }

        this.fieldInstance.setFeedback({
          type: 'error',
          code: 'UploadError',
          messages: message ? [message] : [],
        });
      } catch (e) {
        console.warn('设置 Formily 错误反馈失败:', e);
      }
    },

    // 动态设置组件属性
    setFormilyComponentProps(props) {
      if (!this.fieldInstance || !this.fieldInstance.setComponentProps) {
        return;
      }

      try {
        this.fieldInstance.setComponentProps(props);
      } catch (e) {
        console.warn('设置 Formily 组件属性失败:', e);
      }
    },

    // ========== van-uploader 回调方法 ==========

    // 文件读取前校验（vant beforeRead）
    // 支持同步和 async 外部校验函数
    // 取值优先级：beforeReadProp（newUpload.js 透传）> $attrs['before-read']（Upload.js 透传）> $listeners['before-read']（@ 写法）
    async beforeRead(file) {
      // ===== 统一获取外部校验函数 =====
      const externalBeforeRead =
        (this.beforeReadProp && typeof this.beforeReadProp === 'function') ? this.beforeReadProp :
        (this.$attrs && typeof this.$attrs['before-read'] === 'function') ? this.$attrs['before-read'] :
        (this.$attrs && typeof this.$attrs.beforeRead === 'function') ? this.$attrs.beforeRead :
        (this.$listeners && typeof this.$listeners['before-read'] === 'function') ? this.$listeners['before-read'] :
        null;

      
      // 支持多文件，file 可能是数组
      const files = Array.isArray(file) ? file : [file];
      for (const f of files) {
        if (f.size > this.maxSize) {
          this.$message
            ? this.$message.error(`上传文件大小不能超过${this.formatFileSize(this.maxSize)}`)
            : Toast.fail(`上传文件大小不能超过${this.formatFileSize(this.maxSize)}`);
          return false;
        }
        // 空文件拦截：iOS 相册选长视频时若系统导出（loading）未完成就返回，
        // 拿到的 File 可能 size 为 0。这类文件无法解码，直接进入压缩会导致
        // 视频加载永远等不到就绪事件而卡在 0%，并污染后续上传，必须在此拦截。
        if (!f.size) {
          Toast.fail('文件内容为空，请等相册加载完成后重新选择');
          return false;
        }
      }

      // 第二步：执行外部传入的自定义校验
      if (externalBeforeRead) {
        try {
          const externalResult = await externalBeforeRead(file);
          if (externalResult === false) {
            throw new Error('外部校验未通过');
          }
        } catch (e) {
          // 必须 re-throw 让 Promise reject 传播给 Vant，
          // 否则 Vant 会将 resolve(false) 视为通过并继续 readFile，导致文件进入列表显示"上传中"
          throw e;
        }
      } else {
      }
      return file;
    },

    // 文件读取后回调（替代 el-upload 的 on-change）
    // multiple 多选时 file 为数组，需统一入队后串行上传
    async afterRead(file) {
      console.log(file, 'afterRead');

      // ✅ 检查禁用状态：如果禁用则直接返回，不执行上传
      if (this.disabled) {
        console.log('组件已禁用，取消上传');
        return;
      }

      // 清除 Formily 错误反馈
      this.setFormilyFeedback();

      // vant 多选时 file 为数组，单选时为对象，统一处理为数组
      const fileItems = Array.isArray(file) ? file : [file];

      // 全屏遮罩：开启后覆盖「压缩 + 全部文件上传」整个周期，期间不可点击提交按钮
      // 注意不要等入队后才开，压缩阶段同样应被遮罩覆盖
      if (this.blockingLoading) {
        showUploadBlockMask('文件上传中，请勿离开页面');
      }

      try {
        // 若配置了压缩，则在入队前串行完成视频压缩（就地替换 item.file）
        // failedItems：文件未就绪（相册导出未完成）被剔除的项，不入队、不上传
        const failedItems = [];
        if (this.effectiveCompressFormat && this.effectiveCompressFormat !== 'none') {
          for (const item of fileItems) {
            if (!item || !item.file) continue;
            if (!willTransform(item.file, this.effectiveCompressFormat)) continue;
            try {
              if (this.blockingLoading) {
                setUploadBlockMaskText('文件处理中，请勿离开页面');
              }
              // eslint-disable-next-line no-await-in-loop
              await this.compressItemMedia(item);
            } catch (e) {
              // 压缩失败已在 compressItemMedia 内部 Toast 提示，此处保持原文件继续上传
              console.warn('[compressMedia] 视频压缩失败，使用原文件上传：', e);
              // 文件未就绪（相册导出未完成，等了 30s 仍不完整）：不得降级上传原文件，
              // 从列表中剔除该项并提示重新选择（compressItemMedia 已 Toast 过细节）
              const code = e && e.code;
              if (code === 'FILE_NOT_READY' || code === 'EMPTY_FILE') {
                failedItems.push(item);
              }
            }
          }
          if (failedItems.length > 0) {
            // vant v-model 列表：从 vanFileList 移除对应项，避免留下 uploading 占位
            this.vanFileList = this.vanFileList.filter((it) => !failedItems.includes(it));
            Toast.fail({
              message: failedItems.length === 1
                ? '视频尚未就绪（相册导出未完成），请重新选择'
                : `${failedItems.length} 个视频尚未就绪（相册导出未完成），请重新选择`,
              duration: 2500,
            });
          }
        }

        fileItems.forEach((item) => {
          if (!item || !item.file) return;
          // 未就绪被剔除的文件不再入队上传
          if (failedItems && failedItems.includes(item)) return;
          // 设置文件状态为上传中
          item.status = 'uploading';
          item.message = '上传中...';
          // 推入队列
          this.uploadQueue.push(item);
        });

        // 启动串行队列处理（await 保证遮罩覆盖到队列全部消费完）
        if (this.blockingLoading) {
          setUploadBlockMaskText('文件上传中，请勿离开页面');
        }
        await this.processUploadQueue();
      } finally {
        if (this.blockingLoading) {
          hideUploadBlockMask();
        }
      }
    },

    // 就地压缩 item 中的视频文件（GIF 或视频同格式压缩，由 compressFormat 决定）
    // 转换过程用 Toast.loading 展示进度；成功后 Toast.success，失败 Toast.fail 并向外抛错。
    // 副作用：改写 item.file / item.name / item.isImage / item.content / item._keepLocalPreview
    async compressItemMedia(item) {
      // 七彩石总开关兜底：即便上层判断被绕过，开关关闭时也不执行任何压缩，直接原样返回
      if (!this.enableMediaCompress) {
        return item;
      }
      const origFile = item.file;
      const startTs = Date.now();
      // 全屏遮罩开启时，压缩进度合并进遮罩文案，不再单独弹 Toast.loading。
      // 原因：遮罩为半透明，底层 Toast 会透出来，形成"两个 loading"叠加的观感。
      const useBlockMask = !!this.blockingLoading;
      const loading = useBlockMask
        ? { message: '', clear() { /* 遮罩模式下无 Toast，空实现占位 */ } }
        : Toast.loading({
          message: '视频压缩中(0%)...',
          forbidClick: true,
          duration: 0,
        });
      // 统一出口：遮罩模式下更新遮罩文案，否则更新 Toast 文案
      const setCompressTip = (msg) => {
        if (useBlockMask) {
          setUploadBlockMaskText(msg);
        } else {
          loading.message = msg;
        }
      };
      // 整体超时兜底：分管线设定不同上限
      //   ffmpeg.wasm 管线：确认走 ffmpeg 后收紧到 60s（20s 加载 + 转码，卡住基本无解）
      //   MediaRecorder 管线：处理时长 ≈ 视频时长/4（4x 倍速播放），5 分钟视频约 75s，兜底 180s
      //   起始阶段还不知道走哪条管线，先用宽松上限，进入具体 stage 后再重置
      let overallTimer = null;
      let currentTimeoutMs = 180 * 1000;
      let overallRejecter = null;
      // 超时后真正取消底层转换（worker/gif 编码），避免后台空转浪费移动端资源
      const abortController = new AbortController();
      // 注意：不能在 Promise executor 内引用 overallTimeout（const 尚未初始化会抛 TDZ
      // ReferenceError，导致压缩必现失败），因此 executor 只保存 reject，resetTimeout 在
      // 构造完成后挂载。
      const overallTimeout = new Promise((_, reject) => {
        overallRejecter = reject;
      });
      overallTimeout.resetTimeout = (ms) => {
        if (overallTimer) clearTimeout(overallTimer);
        currentTimeoutMs = ms;
        overallTimer = setTimeout(() => {
          console.warn(`[compressMedia] 整体超时触发（${Math.round(ms / 1000)}s），取消底层转换`);
          try { abortController.abort(); } catch (_) { /* ignore */ }
          const err = new Error(`视频压缩整体超时（${Math.round(ms / 1000)}s）`);
          err.code = 'COMPRESS_OVERALL_TIMEOUT';
          overallRejecter && overallRejecter(err);
        }, ms);
      };
      overallTimeout.resetTimeout(currentTimeoutMs);
      try {
        const { file: outFile, kind } = await Promise.race([
      compressMedia(origFile, {
        format: this.effectiveCompressFormat,
            ratio: this.compressRatio,
            signal: abortController.signal,
            onStage: (stage) => {
              console.log('[compressMedia] stage:', stage);
              // 更细的阶段提示，避免真机上用户误以为卡死：
              //   video / gif           管线首帧（尚未确定具体实现）
              //   load-ffmpeg-import    ffmpeg 门面 JS 下载
              //   load-ffmpeg-core      ffmpeg 内核 wasm 下载（~30MB）
              //   transcoding           ffmpeg 转码中
              //   mr-video / mr-prepare / mr-recording  MediaRecorder 管线阶段
              //   prepare               文件就绪等待（iOS 相册导出未完成时的轮询）
              if (stage === 'prepare') {
                setCompressTip('视频准备中（相册导出未完成），请稍候...');
              } else if (stage === 'load-ffmpeg-import') {
                setCompressTip('正在下载压缩组件...');
                // 确认走 ffmpeg 管线：收紧超时到 60s
                overallTimeout.resetTimeout && overallTimeout.resetTimeout(60 * 1000);
              } else if (stage === 'load-ffmpeg-core') {
                setCompressTip('正在下载编码器（约30MB，首次较慢）...');
              } else if (stage === 'transcoding') {
                setCompressTip('视频压缩中(0%)...');
              } else if (stage === 'video') {
                setCompressTip('视频压缩准备中...');
              } else if (stage === 'gif') {
                setCompressTip('视频转 GIF 中(0%)...');
              } else if (stage === 'mr-video' || stage === 'mr-prepare') {
                // MediaRecorder 管线：秒级启动，无需大文件下载
                setCompressTip('正在准备视频压缩...');
              } else if (stage === 'mr-recording') {
                setCompressTip('视频压缩中(0%)...');
              }
            },
            onProgress: (p) => {
              const percent = Math.min(99, Math.max(0, Math.round(p * 100)));
              // 使用局部变量替代闭包中的 kind（此时外层还未拿到 kind）
              setCompressTip(`视频压缩中(${percent}%)...`);
            },
          }),
          overallTimeout,
        ]);

        // 替换 item 中的文件，让下游 uploadQueue 无感透传
        item.file = outFile;
        item.name = outFile.name || item.name;
        // 缩略图与预览标志：GIF 走图片预览；视频走视频占位（vant 判断 isImage=false 时显示视频缩略）
        item.isImage = kind === 'gif';

        // 生成本地 base64 缩略图，避免上传完成后 TUS uploadLink 无法直连造成裂图
        // - GIF：base64 直接作为 <img> 缩略图与全屏预览的图片源
        // - 视频：base64 存下备用（比如未来做视频封面/自定义预览），当前仅用于兜底
        try {
          item.content = await this.readFileAsDataURL(outFile);
          item._keepLocalPreview = true;
          // 视频类型：额外生成 blob URL 作为本地预览兜底
          // （远端 url 若无法直连播放，可用此本地 URL 直接播）
          if (kind !== 'gif' && typeof URL !== 'undefined' && URL.createObjectURL) {
            try {
              item._localBlobUrl = URL.createObjectURL(outFile);
            } catch (_) { /* ignore */ }
          }
        } catch (readErr) {
          console.warn('[compressMedia] 生成本地缩略图 base64 失败（不影响上传）：', readErr);
        }

        if (useBlockMask) {
          // 遮罩模式下不弹 Toast：遮罩此时仍在，Toast 会透出来形成双层提示
          setUploadBlockMaskText('文件上传中，请勿离开页面');
        } else {
          Toast.clear();
          Toast.success({
            message: `压缩完成（${this.formatFileSize(origFile.size)} → ${this.formatFileSize(outFile.size)}）`,
            duration: 1500,
          });
        }
        console.log('[compressMedia] 成功', {
          kind,
          from: origFile.size,
          to: outFile.size,
          costMs: Date.now() - startTs,
        });
      } catch (err) {
        if (useBlockMask) {
          // 遮罩模式下静默降级，错误提示交由遮罩文案承载
          console.warn('[compressMedia] 压缩失败，已改用原文件上传：', err && err.message);
        } else {
          Toast.clear();
          // 根据错误类型给出不同提示，让用户知道会自动降级
          const code = err && err.code;
          let msg = '视频压缩失败，已改用原文件上传';
          if (code === 'FFMPEG_UNSUPPORTED') {
            msg = '当前浏览器不支持压缩，已改用原文件上传';
          } else if (code === 'EMPTY_FILE' || code === 'FILE_NOT_READY') {
            msg = '视频文件未就绪（相册导出未完成），请重新选择';
          } else if (code === 'VIDEO_NO_SOURCE') {
            msg = '无法解析该视频（iOS 相册视频可能无法直接读取），请重新选择或更换视频';
          } else if (code === 'FFMPEG_TIMEOUT' || code === 'COMPRESS_OVERALL_TIMEOUT') {
            msg = '压缩超时，已改用原文件上传';
          }
          Toast.fail({ message: msg, duration: 2000 });
        }
        console.error('[compressMedia] 失败', err);

        // 降级：给原文件也补一份本地 blob URL 兜底预览
        // 真机上上传成功后返回的 url 可能因跨域/鉴权/域名替换等无法直连播放，
        // 此时用本地 blob URL 至少能在当前会话内预览。
        try {
          if (typeof URL !== 'undefined' && URL.createObjectURL) {
            item._localBlobUrl = URL.createObjectURL(origFile);
          }
        } catch (_) { /* ignore */ }
        throw err;
      } finally {
        if (overallTimer) clearTimeout(overallTimer);
      }
    },

    // 把 File/Blob 读成 base64 dataURL，供 vant Uploader 缩略图使用
    readFileAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('读取文件失败'));
        reader.readAsDataURL(file);
      });
    },

    // ========== 多文件上传队列处理 ==========

    // 串行处理上传队列：保证同一时刻只有一个文件在上传，避免共享状态竞态
    async processUploadQueue() {
      if (this.isProcessingQueue) {
        // 已经在处理中，新文件已入队，会被自动消费
        return;
      }
      this.isProcessingQueue = true;
      try {
        while (this.uploadQueue.length > 0) {
          const fileItem = this.uploadQueue.shift();
          await this.uploadSingleVanFile(fileItem);
        }
      } finally {
        this.isProcessingQueue = false;
      }
    },

    // 上传单个 vant 文件项（在队列中被串行调用）
    async uploadSingleVanFile(fileItem) {
      if (!fileItem || !fileItem.file) {
        return;
      }

      // 初始化当前文件的上传状态
      this.successData = fileItem;
      this.file = fileItem.file;
      this.statusMessage = '';
      this.uploadedBytes = 0;
      this.progress = 0;
      this.uploadLink = null;
      this.uploadUrl = null;

      await this.startUpload(fileItem);
    },

    // 文件大小超出限制
    onOversize() {
      Toast.fail(`上传文件大小不能超过${this.formatFileSize(this.maxSize)}`);
    },

    // 删除文件前回调（替代 el-upload 的 on-remove）
    beforeDelete(file, detail) {
      console.log(file, 'beforeDelete');

      // 释放本地 blob URL（防止内存泄漏）——由 compressItemMedia 生成
      if (file && file._localBlobUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) {
        try { URL.revokeObjectURL(file._localBlobUrl); } catch (_) { /* ignore */ }
        file._localBlobUrl = '';
      }

      // 支持自定义 onRemove 回调
      if (this.onRemoveProp && typeof this.onRemoveProp === 'function') {
        this.onRemoveProp(file);
      }

      // 从 emitData 中移除对应文件
      let removeIndex = this.emitData.findIndex(item => item.uid === file.uid || item.uid === file._emitDataUid);
      // 备选匹配：通过 name 和 url 匹配（应对刷新页面后 uid 不一致的情况）
      if (removeIndex === -1) {
        removeIndex = this.emitData.findIndex(item => {
          const itemUrl = item.response && item.response.FileList && item.response.FileList[0] && item.response.FileList[0].url;
          // 通过文件名匹配，同时比较 url（考虑域名替换后的情况）
          return item.name === file.name && (itemUrl === file.url || this.replaceDomain(itemUrl) === file.url);
        });
      }
      if (removeIndex !== -1) {
        this.emitData.splice(removeIndex, 1);
      }

      // 如果是通过 watch 初始化的已有文件，也需要从 fileUplaod 中移除
      if (this.fileUplaod && this.fileUplaod.length) {
        const fIdx = this.fileUplaod.findIndex(item => item.name === file.name && item.url === file.url);
        if (fIdx !== -1) {
          this.fileUplaod.splice(fIdx, 1);
        }
      }

      // emit 时传出纯净数据（不含内部标记），避免 JSON.stringify 循环引用
      this.$emit('change', this.emitData.map(item => ({
        name: item.name,
        response: item.response,
      })));
      // 清除 Formily 错误反馈
      this.setFormilyFeedback();

      return true; // 返回 true 允许删除
    },

    // 预览文件
    async handlePreview(file) {
      // 支持自定义 onPreview 回调
      if (this.onPreviewProp && typeof this.onPreviewProp === 'function') {
        this.onPreviewProp(file);
        return;
      }

      // 支持 Formily componentProps 中的 onPreview
      if (this.fieldInstance && this.fieldInstance.componentProps && this.fieldInstance.componentProps.onPreview) {
        this.fieldInstance.componentProps.onPreview(file);
        return;
      }

      // 默认行为：图片类型在当前页面预览，非图片类型打开新页面
      // 若该项存在本地 base64（如视频转 GIF 场景），远端 url 可能无法直连图片，
      // 优先使用本地 content 避免全屏预览裂图
      // 视频类型：优先使用 _localBlobUrl（compressItemMedia 里生成的本地 blob URL），
      // 真机上远端 tus uploadLink 常因鉴权/跨域/域名替换等无法直连播放，本地 blob URL 是可靠兜底
      const isVideoName = /\.(mp4|mov|avi|mkv|webm|m4v|3gp|flv|wmv)$/i.test(file.name || '');
      let url;
      if (file._keepLocalPreview && file.content) {
        url = file.content;
      } else if (isVideoName && file._localBlobUrl) {
        url = file._localBlobUrl;
      } else {
        url = file.url || file.content || file._localBlobUrl;
        url = this.replaceDomain(url);
      }
      if (url) {
        // 判断是否为图片：优先使用 isImage 标记，其次根据文件名/URL后缀判断
        const isImage = file.isImage || this.isImageByName(file.name || url);
        if (isImage) {
          // 图片类型：使用 vant ImagePreview 在当前页面全屏预览，附带"提取文字"按钮
          const imageUrls = this.vanFileList
            .filter(f => f.isImage || this.isImageByName(f.name || f.url))
            .map(f => (f._keepLocalPreview && f.content) ? f.content : f.url);
          const startIndex = imageUrls.indexOf(url);
          const previewInstance = ImagePreview({
            images: imageUrls,
            startPosition: startIndex >= 0 ? startIndex : 0,
            closeable: true,
          });

          // 在预览界面右下角注入"提取文字"按钮
          this.$nextTick(() => {
            this._injectOcrButton(previewInstance, imageUrls, startIndex >= 0 ? startIndex : 0);
          });
        } else {
          // 非图片类型：根据 usePopupPreview 配置决定预览方式
          if (this.usePopupPreview) {
            // 视频类型识别：走 <video> 元素直接播放，避免 iframe 无法内嵌视频链接
            const isVideo = isVideoName || /^blob:/i.test(url || '') || /\.(mp4|webm|ogv)(\?|$)/i.test(url || '');

            // 企微场景：视频若已有可靠的本地/远端直链，直接用弹窗内的 <video> 播放体验更好；
            // 其它文件类型仍优先用企微原生 previewFile（内嵌浏览器体验更佳）
            if (this.isWechatEnv && !isVideo) {
              const wxSuccess = await this.previewFileByWx(url, file.name || '');
              if (wxSuccess) return; // 企微预览成功，无需弹窗
              // 企微预览失败，回退到 iframe 弹窗模式
            }

            // 弹窗模式：统一用 van-popup 展示，视频类型用 <video>，其它类型用 iframe，底部保留下载/打开按钮
            this.filePreviewUrl = url;
            this.filePreviewName = file.name || '';
            this.filePreviewIcon = this.getFileIcon(file.name);
            this.filePreviewLoading = true;
            this.filePreviewLoadError = false;
            this.filePreviewIframeVisible = true;
            this.filePreviewIsVideo = isVideo;
            this.showFilePreviewPopup = true;

            // 超时兜底：iframe 5s / video 15s 仍在 loading 则判定无法内联渲染，显示兜底卡片
            // 视频加载 metadata 通常比 iframe 加载慢一些，给更长的超时时间
            if (this._filePreviewTimer) clearTimeout(this._filePreviewTimer);
            const timeoutMs = isVideo ? 15000 : 5000;
            this._filePreviewTimer = setTimeout(() => {
              if (this.filePreviewLoading) {
                this.filePreviewLoading = false;
                this.filePreviewLoadError = true;
              }
            }, timeoutMs);
          } else {
            // 新窗口模式（旧行为）
            window.open(url);
          }
        }
      }
    },

    // 在 ImagePreview 界面注入"提取文字"按钮
    _injectOcrButton(previewInstance, imageUrls, currentIndex) {
      const self = this;
      // 等待 DOM 渲染
      setTimeout(() => {
        const previewEl = document.querySelector('.van-image-preview');
        if (!previewEl) return;

        // 避免重复注入
        if (previewEl.querySelector('.ocr-extract-btn')) return;

        const btn = document.createElement('div');
        btn.className = 'ocr-extract-btn';
        btn.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 7V5C3 3.9 3.9 3 5 3H7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M17 3H19C20.1 3 21 3.9 21 5V7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M21 17V19C21 20.1 20.1 21 19 21H17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <path d="M7 21H5C3.9 21 3 20.1 3 19V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="7" y1="8.5" x2="17" y2="8.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            <line x1="7" y1="12" x2="15" y2="12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            <line x1="7" y1="15.5" x2="12" y2="15.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
          <span>提取文字</span>
        `;

        // 样式
        btn.style.cssText = `
          position: fixed;
          right: 16px;
          bottom: 60px;
          z-index: 2100;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: rgba(25, 137, 250, 0.9);
          color: #fff;
          border-radius: 24px;
          font-size: 14px;
          cursor: pointer;
          box-shadow: 0 2px 12px rgba(25, 137, 250, 0.4);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          user-select: none;
          transition: all 0.2s;
        `;

        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          // 获取当前预览的图片index
          const swiperItems = previewEl.querySelectorAll('.van-image-preview__index');
          let idx = currentIndex;
          if (swiperItems.length > 0) {
            const indexText = swiperItems[0].textContent; // "1 / 3" 格式
            const match = indexText.match(/(\d+)/);
            if (match) idx = parseInt(match[1]) - 1;
          }
          const targetUrl = imageUrls[idx] || imageUrls[currentIndex];

          // 关闭预览
          if (previewInstance && previewInstance.close) {
            previewInstance.close();
          }

          // 打开 OCR 弹窗
          self.startOcrExtract(targetUrl);

          // Aegis 上报：点击提取文字按钮
          self.reportOcrAegis({ action: 'ocr_extract_click', imageUrl: targetUrl });
        });

        btn.addEventListener('touchstart', () => {
          btn.style.transform = 'scale(0.95)';
          btn.style.opacity = '0.8';
        });
        btn.addEventListener('touchend', () => {
          btn.style.transform = 'scale(1)';
          btn.style.opacity = '1';
        });

        previewEl.appendChild(btn);
      }, 300);
    },

    // 开始OCR文字提取
    async startOcrExtract(imageUrl) {
      this.ocrPreviewImage = imageUrl;
      this.ocrStatus = 'scanning';
      this.ocrRawText = '';
      this.showOcrDialog = true;

      // Aegis 上报：OCR 识别开始
      this.reportOcrAegis({ action: 'ocr_recognition_start_upload', imageUrl });

      try {
        const response = await ocrImage(imageUrl, '', {}, store.getters.getConfigItem('ocrFlowKey') || '');

        if (response?.data?.data?.AgentResponse?.Result) {
          const ocrResult = response.data.data.AgentResponse.Result;
          this.ocrRawText = ocrResult;
          this.ocrStatus = 'completed';
          // Aegis 上报：OCR 识别成功
          this.reportOcrAegis({ action: 'ocr_recognition_success', imageUrl, textLength: ocrResult.length });
        } else {
          Toast('OCR识别失败，请确保图片中包含文字信息');
          console.error('OCR识别失败:', response?.data?.data?.AgentResponse);
          // Aegis 上报：OCR 识别失败
          this.reportOcrAegis({ action: 'ocr_recognition_failed', imageUrl, reason: 'empty_result' });
          this.showOcrDialog = false;
        }
      } catch (error) {
        console.error('OCR识别失败:', error);
        Toast('OCR识别失败，请确保图片中包含文字信息');
        // Aegis 上报：OCR 识别异常
        this.reportOcrAegis({ action: 'ocr_recognition_error', imageUrl, error: error.message });
        this.showOcrDialog = false;
      }
    },

    // 预览OCR图片
    previewOcrImage() {
      // Aegis 上报：预览 OCR 图片
      this.reportOcrAegis({ action: 'ocr_image_preview', imageUrl: this.ocrPreviewImage });
      ImagePreview({
        images: [this.ocrPreviewImage],
        startPosition: 0,
        closeable: true,
      });
    },

    // 复制OCR文本
    async copyOcrText() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.ocrRawText);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = this.ocrRawText;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        Toast.success('复制成功');
        // Aegis 上报：复制 OCR 文本成功
        this.reportOcrAegis({ action: 'ocr_text_copy_success', textLength: this.ocrRawText.length });
      } catch (error) {
        console.error('复制失败:', error);
        Toast.fail('复制失败，请手动复制');
        // Aegis 上报：复制 OCR 文本失败
        this.reportOcrAegis({ action: 'ocr_text_copy_failed', error: error.message });
      }
    },

    // 处理用户手动选择并复制OCR文本
    handleManualCopy(event) {
      try {
        // 获取用户选中的文本
        const selection = window.getSelection();
        const selectedText = selection ? selection.toString() : '';

        if (selectedText) {
          // Aegis 上报：用户手动选择并复制 OCR 文本
          this.reportOcrAegis({
            action: 'ocr_text_manual_copy',
            textLength: selectedText.length,
            totalLength: this.ocrRawText.length,
            isPartial: selectedText.length < this.ocrRawText.length
          });
        }
      } catch (e) {
        console.error('[NewUpload] 处理手动复制事件失败:', e);
      }
    },

    // OCR弹窗关闭回调
    handleOcrDialogClosed() {
      // Aegis 上报：OCR 弹窗关闭
      this.reportOcrAegis({ action: 'ocr_dialog_closed' });
      this.ocrRawText = '';
      this.ocrPreviewImage = '';
      this.ocrStatus = 'scanning';
    },

    // Aegis OCR 上报封装方法
    reportOcrAegis(data) {
      try {
        const aegis = this.$store?.state?.user?.aegis;
        if (!aegis) return;
        aegis.infoAll({
          msg: JSON.stringify({
            ...data,
            component: 'NewUpload',
            userid: sessionStorage.getItem('userid') || '',
            timestamp: new Date().toISOString(),
          }),
          ext1: 'upload-ocr',
          trace: `ocr-${data.action}`,
        });
      } catch (e) {
        console.error('[NewUpload] Aegis OCR上报失败:', e);
      }
    },

    // ========== 原有方法 ==========

    // 获取AI审核状态图标
    getStatusIcon(status) {
      const icons = {
        checking: 'el-icon-loading',
        success: 'el-icon-success',
        error: 'el-icon-error'
      };
      return icons[status] || '';
    },

    // 逐张图片AI审核方法（与 Upload.js 保持一致）
    // 每张图片上传完成后立即对该张图片进行审核，审核失败则直接移除该图片
    async singleImageAiCheck(imageUrl, addData, vanFile) {
      if (!this.is_ok || !imageUrl) {
        return;
      }

      // 显示AI审核中的提示，并禁用上传按钮
      this.isAiChecking = true;
      this.checkStatus = 'checking';
      this.checkMessage = 'AI正在审核图片...';

      // 全屏遮罩开启时，审核提示合并进遮罩文案，避免与遮罩叠加成两层
      const useBlockMaskAi = !!this.blockingLoading;
      const loadingToast = useBlockMaskAi
        ? { clear() { /* 遮罩模式下无 Toast，空实现占位 */ } }
        : Toast.loading({
          message: 'AI正在审核图片...',
          forbidClick: true,
          duration: 0,
        });
      if (useBlockMaskAi) {
        setUploadBlockMaskText('AI正在审核图片，请勿离开页面');
      }

      // 禁用上传按钮（Formily 集成）
      this.setFormilyComponentProps({ disabled: true });

      try {
        const processDefinitionKey = this.is_ok;
        const aiAgentData = this.ai_agent_data || '';

        console.log('开始逐张AI审核，图片URL：', imageUrl, '，ProcessDefinitionKey：', processDefinitionKey);

        const result = await callAiAgentPhotoCheck(imageUrl, processDefinitionKey, aiAgentData);
        console.log('AI审核返回结果：', result);

        // 解析 AgentResponse 中的 Result
        const agentResultStr = result?.data?.AgentResponse?.Result;
        if (agentResultStr) {
          try {
            const agentResult = JSON.parse(agentResultStr);
            console.log('AI审核结果：', agentResult);

            // 判断 is_ok 是否为 true（兼容字符串和布尔值，与 Upload.js 保持一致）
            const isOk = agentResult.is_ok === true || agentResult.is_ok === 'true';
            if (!isOk) {
              // AI审核失败：移除当前上传的图片（与 Upload.js 保持一致）
              this.checkStatus = 'error';
              this.checkMessage = `AI审核失败：${agentResult.reason || '未知原因'}`;
              Toast.fail(`AI审核失败：${agentResult.reason || '未知原因'}`);

              // 从 emitData 中移除当前文件
              const removeIndex = this.emitData.findIndex(item => item.uid === addData.uid);
              if (removeIndex !== -1) {
                this.emitData.splice(removeIndex, 1);
              }

              // 从 vanFileList 中移除当前文件
              if (vanFile) {
                const vanIndex = this.vanFileList.findIndex(f => f === vanFile || f.uid === vanFile.uid);
                if (vanIndex !== -1) {
                  this.vanFileList.splice(vanIndex, 1);
                }
              }

              // 重新 emit change 事件
              this.$emit('change', this.emitData.map(item => ({
                name: item.name,
                response: item.response,
              })));

              // 设置 Formily 错误反馈
              this.setFormilyFeedback({ message: `AI审核失败：${agentResult.reason || '未知原因'}` });

              this.$emit('imgChange', agentResult);
            } else {
              // AI审核成功
              this.checkStatus = 'success';
              this.checkMessage = 'AI审核成功';
              Toast.success('AI审核成功');

              // 清除 Formily 错误反馈
              this.setFormilyFeedback();

              this.$emit('imgChange', agentResult);
            }

            // 3秒后清除状态
            setTimeout(() => {
              this.checkStatus = '';
              this.checkMessage = '';
            }, 3000);

          } catch (parseError) {
            if (parseError.message && parseError.message.includes('AI审核失败')) {
              throw parseError;
            }
            console.error('解析AI审核结果失败：', parseError);
            this.checkStatus = '';
            this.checkMessage = '';
          }
        }
      } catch (checkError) {
        console.error('AI审核请求失败：', checkError);
        if (!checkError.message || !checkError.message.includes('AI审核失败')) {
          this.checkStatus = 'error';
          this.checkMessage = 'AI审核请求失败';
          Toast.fail('AI审核请求失败');
          this.setFormilyFeedback({ message: 'AI审核请求失败' });

          // 3秒后清除错误状态
          setTimeout(() => {
            this.checkStatus = '';
            this.checkMessage = '';
          }, 3000);
        }
      } finally {
        // 无论成功还是失败，都关闭加载提示并恢复上传按钮
        loadingToast.clear();
        this.isAiChecking = false;
        // 恢复上传按钮（Formily 集成）
        this.setFormilyComponentProps({ disabled: false });
      }
    },

    // AI图片审核方法（批量审核，保留作为备选）
    async startAiCheck() {
      if (!this.is_ok || this.isAiChecking) {
        return;
      }

      // 清除之前的定时器
      if (this.uploadCompleteTimer) {
        clearTimeout(this.uploadCompleteTimer);
      }

      // 设置防抖定时器，等待500ms确保所有图片都上传完成
      this.uploadCompleteTimer = setTimeout(async () => {
        // 检查是否所有文件都已上传完成
        const allFilesUploaded = this.emitData.every(item =>
          item.status === 'success' || item.url || item.response
        );

        if (!allFilesUploaded || this.isAiChecking) {
          return;
        }

        // 标记正在进行AI审核
        this.isAiChecking = true;
        this.checkStatus = 'checking';
        this.checkMessage = 'AI正在审核图片...';

        // 禁用上传按钮（Formily 集成）
        this.setFormilyComponentProps({ disabled: true });

        Toast('AI正在审核图片...');

        try {
          // 获取所有上传成功的图片链接，组成数组
          const imageUrls = this.emitData
            .filter(item => item.status === 'success' || item.url || item.response)
            .map(item => {
              if (item.response && item.response.FileList && item.response.FileList[0]) {
                return item.response.FileList[0].url;
              }
              return item.url || '';
            })
            .filter(url => url); // 过滤掉空的URL

          if (imageUrls.length === 0) {
            this.checkStatus = '';
            this.checkMessage = '';
            return;
          }

          const imageUrl = JSON.stringify(imageUrls);
          const processDefinitionKey = this.is_ok;
          const aiAgentData = this.ai_agent_data || '';

          console.log('开始调用AI审核，图片数量：', imageUrls.length, '，ProcessDefinitionKey：', processDefinitionKey);

          const result = await callAiAgentPhotoCheck(imageUrl, processDefinitionKey, aiAgentData);
          console.log('AI审核返回结果：', result);

          // 解析 AgentResponse.Result
          const agentResultStr = result?.data?.AgentResponse?.Result;
          if (agentResultStr) {
            try {
              const agentResult = JSON.parse(agentResultStr);
              console.log('AI审核结果：', agentResult);

              // 判断 is_ok 是否为 true
              if (agentResult.is_ok !== true) {
                // AI审核失败
                this.checkStatus = 'error';
                this.checkMessage = `AI审核失败：${agentResult.reason || '未知原因'}`;
                Toast.fail(`AI审核失败：${agentResult.reason || '未知原因'}`);

                // 设置 Formily 错误反馈
                this.setFormilyFeedback({ message: `AI审核失败：${agentResult.reason || '未知原因'}` });

                this.$emit('imgChange', agentResult);
              } else {
                // AI审核成功
                this.checkStatus = 'success';
                this.checkMessage = 'AI审核成功';
                Toast.success('AI审核成功');

                // 清除 Formily 错误反馈
                this.setFormilyFeedback();

                this.$emit('imgChange', agentResult);
              }

              // 3秒后清除状态
              setTimeout(() => {
                this.checkStatus = '';
                this.checkMessage = '';
              }, 3000);

            } catch (parseError) {
              console.error('解析AI审核结果失败：', parseError);
              this.checkStatus = '';
              this.checkMessage = '';
            }
          } else {
            // 处理AgentResponse，如果是对象则转换为JSON字符串
            let errorMsg = '未知原因';
            if (result?.data?.AgentResponse) {
              if (typeof result.data.AgentResponse === 'object') {
                errorMsg = JSON.stringify(result.data.AgentResponse, null, 2);
              } else {
                errorMsg = result.data.AgentResponse;
              }
            }
            Toast.fail(`AI审核失败：${errorMsg}`);
            this.checkStatus = '';
            this.checkMessage = '';
          }
        } catch (error) {
          console.error('AI审核请求失败：', error);
          this.checkStatus = 'error';
          this.checkMessage = 'AI审核请求失败';
          Toast.fail('AI审核请求失败');

          // 设置 Formily 错误反馈
          this.setFormilyFeedback({ message: 'AI审核请求失败' });

          // 3秒后清除错误状态
          setTimeout(() => {
            this.checkStatus = '';
            this.checkMessage = '';
          }, 3000);
        } finally {
          // 恢复上传按钮（Formily 集成）
          this.setFormilyComponentProps({ disabled: false });
          this.isAiChecking = false;
        }
      }, 500); // 500ms防抖延迟
    },

    // 安全关闭弹窗：先断开 iframe 连接，避免卸载时触发页面刷新
    closeFilePreview() {
      // 先把 iframe src 设为 about:blank 断开网络连接
      const iframe = this.$refs.filePreviewIframe;
      if (iframe) {
        try {
          iframe.src = 'about:blank';
        } catch (e) {
          // 忽略跨域错误
        }
      }
      // 隐藏 iframe DOM，延迟一点再关弹窗，让浏览器完成卸载
      this.filePreviewIframeVisible = false;
      this.$nextTick(() => {
        this.showFilePreviewPopup = false;
      });
    },

    // 文件预览弹窗关闭后的清理
    handleFilePreviewClosed() {
      if (this._filePreviewTimer) {
        clearTimeout(this._filePreviewTimer);
        this._filePreviewTimer = null;
      }
      this.filePreviewUrl = '';
      this.filePreviewName = '';
      this.filePreviewLoading = true;
      this.filePreviewLoadError = false;
      this.filePreviewIframeVisible = false;
      this.filePreviewIsVideo = false;
    },

    openFileUploadLink(url) {
      window.open(this.replaceDomain(url));
    },

    openUploadLink(item) {
      const newUrl = this.replaceDomain(item.response.FileList[0].url);
      window.open(newUrl);
    },

    removeFile(index) {
      this.emitData.splice(index, 1);
      this.$emit('change', this.emitData.map(item => ({
        name: item.name,
        response: item.response,
      })));
      this.setFormilyFeedback();
    },

    // ========== 微信/企微选图选文件相关方法（仅 newChooseImage 不为 'none' 且处于微信/企微环境时使用） ==========

    // van-uploader click-upload 事件监听
    // 当 shouldUseWechatApi = true 且不是"已选择浏览器方式"时，拦截默认上传，弹出选择对话框
    onClickUpload(e) {
      // ✅ 检查禁用状态：如果禁用则阻止事件
      if (this.disabled) {
        e && e.stopPropagation && e.stopPropagation();
        e && e.preventDefault && e.preventDefault();
        return; // 禁用状态下不执行任何操作
      }

      if (this.shouldUseWechatApi && !this.shouldTriggerNativeUpload) {
        e && e.stopPropagation && e.stopPropagation();
        e && e.preventDefault && e.preventDefault();
        this.handleClickUpload(e);
      }
    },

    // 显示选择上传方式的对话框（根据 newChooseImage 动态显示选项）
    showUploadMethodDialog() {
      return new Promise((resolve, reject) => {
        // 根据 newChooseImage 配置动态生成选项
        // 浏览器上传选项始终存在
        const actions = [
          { name: '使用浏览器拍照/上传', value: 'browser' }
        ];

        // 根据配置添加企微相关选项（使用规范化后的值）
        if (this.normalizedNewChooseImage === 'both' || this.normalizedNewChooseImage === 'wework') {
          actions.unshift({ name: '使用微信拍照/选图', value: 'wechat' });
        }
        
        if (this.normalizedNewChooseImage === 'both' || this.normalizedNewChooseImage === 'chat') {
          actions.splice(actions.findIndex(a => a.value === 'browser'), 0, 
            { name: '从会话选择文件上传', value: 'wechat-file' });
        }

        const actionSheetVm = new Vue({
          data() {
            return { show: true, actions };
          },
          methods: {
            onSelect(item) {
              this.show = false;
              this.$nextTick(() => {
                this.$destroy();
                this.$el.parentNode && this.$el.parentNode.removeChild(this.$el);
                resolve(item.value);
              });
            },
            onCancel() {
              this.show = false;
              this.$nextTick(() => {
                this.$destroy();
                this.$el.parentNode && this.$el.parentNode.removeChild(this.$el);
                reject(new Error('用户取消选择'));
              });
            },
          },
          render(h) {
            return h('van-action-sheet', {
              props: {
                value: this.show,
                actions: this.actions,
                cancelText: '取消',
              },
              on: {
                input: (val) => { if (!val) this.onCancel(); },
                select: this.onSelect,
                cancel: this.onCancel,
              },
            });
          },
        });

        const mountNode = document.createElement('div');
        document.body.appendChild(mountNode);
        actionSheetVm.$mount(mountNode);
      });
    },

    // 配置微信 JS-SDK（只配置一次）
    async ensureWechatConfig() {
      if (!this.isWechatEnv) return false;
      if (this.wxConfigured) return true;
      if (this.configPromise) return await this.configPromise;

      const self = this;
      this.configPromise = (async () => {
        try {
          const timestamp = Math.round(new Date() / 1000) + '';
          const configData = {
            Url: window.location.href,
            Noncestr: 'otob',
            Timestamp: timestamp,
          };
          const signatureUrl = store.getters.getConfigItem('signature');
          const { data } = await axios.post(signatureUrl, configData);
          const wxApi = typeof ww !== 'undefined' ? ww : wx;

          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('微信 SDK 配置超时'));
            }, 10000);

            wxApi.config({
              beta: true,
              debug: false,
              appId: sessionStorage.getItem('appid') || store.getters.getConfigItem('corpID'),
              timestamp: parseInt(timestamp),
              nonceStr: 'otob',
              signature: data.signature,
              jsApiList: ['chooseImage', 'getLocalImgData', 'agentConfig', 'invoke'],
            });

            wxApi.ready(async () => {
              clearTimeout(timeout);
              self.wxConfigured = true;

              const ua = window.navigator.userAgent.toLowerCase();
              const isWxWork = ua.includes('wxwork');

              if (isWxWork) {
                try {
                  const signatureApiUrl = store.getters.getConfigItem('signatureApi');
                  if (!signatureApiUrl) {
                    console.warn('signatureApi 未配置，跳过 agentConfig 配置');
                    resolve(true);
                    return;
                  }
                  const verifyTimestamp = `${Math.round(new Date() / 1000)}`;
                  const verifyData = {
                    Url: window.location.href,
                    Noncestr: 'otob',
                    Timestamp: verifyTimestamp,
                  };
                  const verifyRes = await axios.post(signatureApiUrl, verifyData, {
                    headers: {
                      authToken: sessionStorage.getItem('AuthToken'),
                      userid: sessionStorage.getItem('userid'),
                    },
                  });
                  console.log('signatureApi 应用签名响应:', verifyRes);

                  if (!verifyRes || !verifyRes.data || !verifyRes.data.signature) {
                    console.warn('agentConfig 签名获取失败，跳过 agentConfig 配置');
                    resolve(true);
                    return;
                  }
                  const verifySignature = verifyRes.data.signature;

                  await new Promise((agentResolve) => {
                    wx.agentConfig({
                      debug: true,
                      corpid: store.getters.getConfigItem('corpID'),
                      agentid: process.env.NODE_ENV === 'development' ? '1000036' : '1000035',
                      nonceStr: 'otob',
                      timestamp: verifyTimestamp * 1,
                      signature: verifySignature,
                      jsApiList: ['chooseMessageFile', 'getLocalFileData'],
                      success: (res) => {
                        console.log('agentConfig 配置成功，支持企微文件上传', res);
                        self.agentConfigured = true;
                        agentResolve(true);
                      },
                      fail: (err) => {
                        console.error('agentConfig 配置失败:', err);
                        self.agentConfigured = false;
                        agentResolve(false);
                      },
                    });
                  });
                  resolve(true);
                } catch (err) {
                  console.warn('获取 agentConfig 签名失败，企微文件上传功能不可用:', err);
                  self.agentConfigured = false;
                  resolve(true);
                }
              } else {
                resolve(true);
              }
            });

            wxApi.error((err) => {
              clearTimeout(timeout);
              console.error('微信 JS-SDK 配置失败', err);
              reject(new Error('微信配置错误: ' + (err.errMsg || '未知错误')));
            });
          });

          return true;
        } catch (err) {
          console.error('配置微信 JS-SDK 失败', err);
          throw err;
        } finally {
          self.configPromise = null;
        }
      })();

      return await this.configPromise;
    },

    // 微信 chooseImage
    chooseImageByWechat() {
      return new Promise((resolve, reject) => {
        const wxApi = typeof ww !== 'undefined' ? ww : wx;
        const maxCount = this.maxCount || 9;
        wxApi.chooseImage({
          count: maxCount,
          sizeType: ['original', 'compressed'],
          sourceType: this.capture === 'camera' ? ['camera'] : ['album', 'camera'],
          success: (res) => resolve(res.localIds || []),
          fail: (err) => {
            console.error('微信选择图片失败:', err);
            reject(err);
          },
          cancel: () => reject(new Error('用户取消选择')),
        });
      });
    },

    // 企微 chooseMessageFile
    chooseFileByWechat() {
      return new Promise((resolve, reject) => {
        const ua = window.navigator.userAgent.toLowerCase();
        const isWxWork = ua.includes('wxwork');
        if (!isWxWork) {
          reject(new Error('当前不在企业微信环境中,请在企微工作台打开'));
          return;
        }
        if (!this.agentConfigured) {
          reject(new Error('企微应用权限未配置,请联系管理员在企微管理后台开启文件上传权限'));
          return;
        }
        const wxApi = typeof wx !== 'undefined' ? wx : (typeof window.wx !== 'undefined' ? window.wx : null);
        if (!wxApi) {
          reject(new Error('企业微信 SDK 未加载,请刷新页面重试'));
          return;
        }
        const maxCount = this.maxCount || 9;
        // chooseMessageFile 的 type 直接使用 fileType 参数传入的值（默认 'file'，与原来一致）
        const chooseType = this.fileType || 'file';
        try {
          wxApi.invoke(
            'chooseMessageFile',
            { count: maxCount, type: chooseType },
            (res) => {
              if (res.err_msg === 'chooseMessageFile:ok') {
                const tempFiles = res.tempFiles || [];
                if (tempFiles.length === 0) {
                  reject(new Error('未选择任何文件'));
                  return;
                }
                resolve({ type: 'file', tempFiles });
              } else if (res.err_msg === 'chooseMessageFile:cancel') {
                reject(new Error('用户取消选择'));
              } else {
                const errMsg = res.err_msg || '选择文件失败';
                if (errMsg.includes('permission') || errMsg.includes('nopermission')) {
                  reject(new Error('无文件选择权限,请联系管理员在企微后台配置应用接口权限'));
                } else if (errMsg.includes('function not exist')) {
                  reject(new Error('当前企业微信版本过低,请升级到最新版本'));
                } else {
                  reject(new Error(errMsg));
                }
              }
            }
          );
        } catch (err) {
          console.error('调用企微文件选择接口异常:', err);
          reject(new Error('调用文件选择功能失败,请重试'));
        }
      });
    },

    // 获取微信选择图片的本地数据
    getLocalImageData(localId) {
      return new Promise((resolve, reject) => {
        const wxApi = typeof ww !== 'undefined' ? ww : wx;
        wxApi.getLocalImgData({
          localId,
          success: (res) => {
            let localData = res.localData;
            if (localData && !localData.startsWith('data:image')) {
              localData = 'data:image/jpeg;base64,' + localData;
            }
            resolve(localData);
          },
          fail: (err) => {
            console.error('获取图片数据失败:', err);
            reject(err);
          },
        });
      });
    },

    // 获取企微选择文件的本地数据
    getLocalFileData(localId) {
      return new Promise((resolve, reject) => {
        const wwApi = typeof ww !== 'undefined' ? ww : (typeof window.ww !== 'undefined' ? window.ww : null);
        const wxApi = typeof wx !== 'undefined' ? wx : (typeof window.wx !== 'undefined' ? window.wx : null);

        if (wwApi && typeof wwApi.getLocalFileData === 'function') {
          wwApi.getLocalFileData({ localId }).then((res) => {
            if (res.errMsg === 'getLocalFileData:ok' || res.localData) {
              resolve(res.localData);
            } else {
              reject(new Error(res.errMsg || '获取文件数据失败'));
            }
          }).catch((err) => {
            reject(new Error(err.errMsg || err.message || '获取文件数据失败'));
          });
          return;
        }
        if (wxApi && typeof wxApi.invoke === 'function') {
          wxApi.invoke('getLocalFileData', { localId }, (res) => {
            if (res.err_msg === 'getLocalFileData:ok') {
              resolve(res.localData);
            } else {
              const errMsg = res.err_msg || '获取文件数据失败';
              if (errMsg.includes('no permission')) {
                reject(new Error('无文件读取权限,请联系管理员配置应用权限'));
              } else if (errMsg.includes('no such file')) {
                reject(new Error('文件不存在或已过期,请重新选择'));
              } else if (errMsg.includes('file exceed size limit')) {
                reject(new Error('文件超过20MB限制,请选择更小的文件'));
              } else {
                reject(new Error(errMsg));
              }
            }
          });
          return;
        }
        reject(new Error('企业微信 SDK 未加载,请刷新页面重试'));
      });
    },

    // base64 -> File
    base64ToFile(base64Data, filename) {
      const arr = base64Data.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },

    // 根据文件名推断 MIME 类型
    getMimeType(fileName) {
      const ext = (fileName.split('.').pop() || '').toLowerCase();
      const map = {
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
        bmp: 'image/bmp', webp: 'image/webp', svg: 'image/svg+xml',
        pdf: 'application/pdf', doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ppt: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        txt: 'text/plain', zip: 'application/zip', rar: 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed', mp4: 'video/mp4', mov: 'video/quicktime',
        m4v: 'video/x-m4v', webm: 'video/webm', avi: 'video/x-msvideo',
        '3gp': 'video/3gpp', mkv: 'video/x-matroska', wmv: 'video/x-ms-wmv',
        flv: 'video/x-flv', mpg: 'video/mpeg', mpeg: 'video/mpeg', ogv: 'video/ogg',
        mp3: 'audio/mpeg', wav: 'audio/wav',
      };
      return map[ext] || 'application/octet-stream';
    },

    // 通过文件内容魔数判断 base64 数据是否为视频（兼容 data: 前缀）
    // 背景：企微 chooseMessageFile 对会话中的视频返回 name='视频'（无扩展名）、
    // type='File'，无法从文件名/MIME 标记识别，只能按文件头判断：
    //   MP4/MOV/M4V/3GP 等 → offset 4~7 为 'ftyp'
    //   WebM/MKV         → EBML 头 1A 45 DF A3
    //   AVI              → 'RIFF' + 'AVI '
    //   FLV              → 'FLV'
    //   MPEG PS          → 00 00 01 BA / 00 00 01 B3
    isVideoBase64(base64Data) {
      try {
        const parts = String(base64Data || '').split(',');
        // 兼容带 data: 前缀（取逗号后）与纯 base64（无逗号，取整串）
        let clean = (parts.length > 1 ? parts[1] : parts[0]) || '';
        // 最多取头部 24 个 base64 字符 ≈ 16 字节（足以覆盖上述魔数长度）
        clean = clean.slice(0, 24);
        // atob 要求长度是 4 的倍数，不足补 padding
        while (clean.length % 4 !== 0) clean += '=';
        const head = atob(clean);
        const c = (i) => head.charCodeAt(i);
        // ftyp（MP4/MOV/M4V/3GP 等）
        if (head.length >= 8 && c(4) === 102 && c(5) === 116 && c(6) === 121 && c(7) === 112) return true;
        // EBML（WebM/MKV）
        if (head.length >= 4 && c(0) === 0x1A && c(1) === 0x45 && c(2) === 0xDF && c(3) === 0xA3) return true;
        // RIFF + AVI（AVI）
        if (head.length >= 12 && head.slice(0, 4) === 'RIFF' && head.slice(8, 11) === 'AVI') return true;
        // FLV
        if (head.length >= 3 && head.slice(0, 3) === 'FLV') return true;
        // MPEG PS（00 00 01 BA / 00 00 01 B3）
        if (head.length >= 4 && c(0) === 0 && c(1) === 0 && c(2) === 1 && (c(3) === 0xBA || c(3) === 0xB3)) return true;
        return false;
      } catch (_) {
        return false;
      }
    },

    // 把通过微信/企微接口拿到的 File 对象，按 vant afterRead 的入参格式包装并依次走 TUS 上传
    // 复用 startUpload，从而保持 TUS 分片/进度/AI 审核/emit 行为完全一致
    async handleWechatPickedFile(file, options = {}) {
      // 诊断日志：定位"选了视频但未触发压缩转换"的问题
      // （compressFormat 配置是否为 gif / 文件是否被识别为视频）
      console.log('[NewUpload] handleWechatPickedFile', {
        name: file.name,
        type: file.type,
        size: file.size,
        compressFormat: this.effectiveCompressFormat,
        enableMediaCompress: this.enableMediaCompress,
        willTransform: willTransform(file, this.effectiveCompressFormat),
      });
      try {
        if (this.blockingLoading) {
          showUploadBlockMask();
        }
        // 若配置了压缩，先做压缩（失败降级为原文件）
        let finalFile = file;
        let convertedContent = '';
        let keepLocalPreview = false;
        let convertedIsImage = null; // null 表示未转换，交由文件名默认判断
        if (willTransform(file, this.effectiveCompressFormat)) {
          try {
            if (this.blockingLoading) {
              setUploadBlockMaskText('文件处理中，请勿离开页面');
            }
            const tempItem = { file, name: file.name };
            await this.compressItemMedia(tempItem);
            finalFile = tempItem.file;
            convertedContent = tempItem.content || '';
            keepLocalPreview = !!tempItem._keepLocalPreview;
            convertedIsImage = !!tempItem.isImage;
          } catch (convErr) {
            // 文件未就绪（相册导出未完成，等了 30s 仍不完整）：不得降级上传原文件
            // —— 原文件同样不完整，传上去也是坏视频。终止本次上传，让用户重新选择。
            // compressItemMedia 内部已 Toast 提示，这里只终止流程。
            const code = convErr && convErr.code;
            if (code === 'FILE_NOT_READY' || code === 'EMPTY_FILE') {
              throw convErr;
            }
            finalFile = file; // 其余失败保持原文件继续上传（原文件本身完整）
          }
        }

        const vanFile = {
          file: finalFile,
          status: 'uploading',
          message: '上传中...',
          name: finalFile.name,
          // 图片才生成预览缩略图（content 为 base64）
          content: convertedContent || options.previewBase64 || '',
          isImage: convertedIsImage !== null ? convertedIsImage : this.isImageByName(finalFile.name),
          // 压缩后本地已有 base64：上传完成后仍用本地渲染，避免 TUS uploadLink 裂图
          _keepLocalPreview: keepLocalPreview,
        };
        // 推入 vant fileList 让组件渲染上传中的占位
        this.vanFileList.push(vanFile);

        // 复用 vant afterRead 的内部状态准备逻辑
        this.successData = vanFile;
        this.file = finalFile;
        this.statusMessage = '';
        this.uploadedBytes = 0;
        this.progress = 0;
        this.uploadLink = null;
        this.uploadUrl = null;

        try {
          await this.startUpload(vanFile);
        } catch (err) {
          // 上传失败：从 vanFileList 中移除占位，避免遗留 uploading 状态
          const idx = this.vanFileList.indexOf(vanFile);
          if (idx !== -1) this.vanFileList.splice(idx, 1);
          throw err;
        }
      } finally {
        // 压缩阶段或更早抛错时同样要解除遮罩，避免遮罩残留锁死页面
        if (this.blockingLoading) {
          hideUploadBlockMask();
        }
      }
    },

    // 辅助函数：触发浏览器原生上传（作为兜底方案）
    async triggerBrowserUpload(event) {
      this.shouldTriggerNativeUpload = true;
      await new Promise((r) => setTimeout(r, 100));
      
      const uploaderEl = (this.$refs.upload && this.$refs.upload.$el) || document.querySelector('.van-uploader');
      if (uploaderEl) {
        const input = uploaderEl.querySelector('.van-uploader__input');
        if (input) {
          input.click();
        }
      }
      
      setTimeout(() => { this.shouldTriggerNativeUpload = false; }, 1000);
    },

    // 处理点击上传按钮（仅微信/企微环境）
    async handleClickUpload(event) {
      try {
        // 根据 newChooseImage 的值决定显示哪些选项
        // 浏览器上传选项始终存在，企微相关选项根据配置显示
        const uploadMethod = await this.showUploadMethodDialog();

        // 浏览器原生
        if (uploadMethod === 'browser') {
          this.shouldTriggerNativeUpload = true;
          await new Promise((r) => setTimeout(r, 100));
          const uploaderEl = (event && event.target && event.target.closest && event.target.closest('.van-uploader'))
            || (this.$refs.upload && this.$refs.upload.$el)
            || document.querySelector('.van-uploader');
          if (uploaderEl) {
            const input = uploaderEl.querySelector('.van-uploader__input');
            if (input) {
              input.click();
            } else {
              Toast.fail('无法打开文件选择器');
            }
          } else {
            Toast.fail('无法打开文件选择器');
          }
          setTimeout(() => { this.shouldTriggerNativeUpload = false; }, 1000);
          return;
        }

        // 微信/企微方式
        const configToast = Toast.loading({
          message: '初始化中...',
          forbidClick: true,
          duration: 0,
        });
        try {
          const configured = await this.ensureWechatConfig();
          configToast.clear();
          if (!configured) {
            // SDK 配置失败时，提供浏览器上传作为兜底选项
            Toast.fail('微信环境初始化失败，正在切换到浏览器上传模式');
            // 切换到浏览器原生上传方式
            this.shouldTriggerNativeUpload = true;
            await new Promise((r) => setTimeout(r, 100));
            
            const uploaderEl = (this.$refs.upload && this.$refs.upload.$el) || document.querySelector('.van-uploader');
            if (uploaderEl) {
              const input = uploaderEl.querySelector('.van-uploader__input');
              if (input) {
                input.click();
              }
            }
            
            setTimeout(() => { this.shouldTriggerNativeUpload = false; }, 1000);
            return;
          }
          await new Promise((r) => setTimeout(r, 300));

          if (uploadMethod === 'wechat-file') {
            try {
              const result = await this.chooseFileByWechat();
              if (result && result.tempFiles && result.tempFiles.length > 0) {
                for (let i = 0; i < result.tempFiles.length; i++) {
                  const tempFile = result.tempFiles[i];
                  const fileName = tempFile.name || `file_${Date.now()}_${i}`;
                  const fileSize = tempFile.size || 0;
                  const localId = tempFile.localId;
                  if (!localId) {
                    Toast.fail('文件数据异常,请重新选择');
                    continue;
                  }
                  // 先校验设计器配置的上限，再校验 getLocalFileData 的 20MB 限制
                  if (this.maxFileSize > 0 && fileSize > this.maxFileSize * 1024 * 1024) {
                    Toast.fail(`文件"${fileName}"大小超过上限，上限为${this.maxFileSize}MB`);
                    continue;
                  }
                  // 企微 getLocalFileData 限制 20MB
                  const maxSize = 20 * 1024 * 1024;
                  if (fileSize > maxSize) {
                    Toast.fail(`文件"${fileName}"超过20MB限制（企微接口限制）`);
                    continue;
                  }
                  try {
                    const base64Data = await this.getLocalFileData(localId);
                    if (!base64Data) {
                      Toast.fail(`文件"${fileName}"获取数据失败`);
                      continue;
                    }
                    let finalBase64 = base64Data;
                    // 企微会话返回的视频 name 可能是'视频'/无扩展名、type 标记为'File'，
                    // 从文件名/MIME 标记都无法识别，最终按文件内容魔数判断（isVideoBase64）
                    const contentIsVideo = this.isVideoBase64(base64Data);
                    if (!base64Data.includes('base64,')) {
                      // 无 data: 前缀：按文件名映射，再依次用 tempFile.type / 内容魔数修正
                      let mimeType = this.getMimeType(fileName);
                      if (tempFile.type === 'video' && !mimeType.startsWith('video/')) {
                        mimeType = 'video/mp4';
                      } else if (tempFile.type === 'image' && !mimeType.startsWith('image/')) {
                        mimeType = 'image/jpeg';
                      }
                      if (!mimeType.startsWith('video/') && contentIsVideo) {
                        mimeType = 'video/mp4';
                      }
                      finalBase64 = `data:${mimeType};base64,${base64Data}`;
                    } else if (contentIsVideo) {
                      // 带 data: 前缀但 MIME 不是 video/*（如 octet-stream）→ 替换前缀修正
                      const prefixMime = (base64Data.match(/^data:([^;]+);base64,/) || [])[1];
                      if (prefixMime && !prefixMime.startsWith('video/')) {
                        finalBase64 = base64Data.replace(/^data:[^;]+;base64,/, 'data:video/mp4;base64,');
                      }
                    }
                    const file = this.base64ToFile(finalBase64, fileName);
                    await this.handleWechatPickedFile(file);
                  } catch (singleErr) {
                    console.error('单个文件处理失败:', singleErr);
                    // silent 错误（文件未就绪等）在 compressItemMedia 内已 Toast 提示，不重复弹
                    if (!singleErr || !singleErr.silent) {
                      Toast.fail(singleErr.message || '文件上传失败');
                    }
                  }
                }
              } else {
                Toast.fail('未选择任何文件');
              }
            } catch (fileErr) {
              if (fileErr.message !== '用户取消选择') {
                // 提供浏览器上传作为兜底
                Toast.fail(`${fileErr.message || '文件选择失败'}，将切换到浏览器上传模式`);
                await new Promise((r) => setTimeout(r, 1500));
                await this.triggerBrowserUpload(event);
              }
            }
          } else {
            // 微信图片
            try {
              const localIds = await this.chooseImageByWechat();
              if (localIds && localIds.length > 0) {
                for (let i = 0; i < localIds.length; i++) {
                  const localId = localIds[i];
                  try {
                    const base64Data = await this.getLocalImageData(localId);
                    const fileName = `wechat_image_${Date.now()}_${i}.jpg`;
                    const file = this.base64ToFile(base64Data, fileName);
                    // 该链路不经过 Vant beforeRead，需单独校验设计器配置的上限
                    if (this.maxFileSize > 0 && file.size > this.maxFileSize * 1024 * 1024) {
                      throw new Error(`图片"${fileName}"大小超过上限，上限为${this.maxFileSize}MB`);
                    }
                    await this.handleWechatPickedFile(file, { previewBase64: base64Data });
                  } catch (singleErr) {
                    console.error('单张图片处理失败:', singleErr);
                    Toast.fail(singleErr.message || '图片上传失败');
                  }
                }
              }
            } catch (imgErr) {
              if (imgErr.message !== '用户取消选择') {
                // 提供浏览器上传作为兜底
                Toast.fail(`${imgErr.message || '图片选择失败'}，将切换到浏览器上传模式`);
                await new Promise((r) => setTimeout(r, 1500));
                await this.triggerBrowserUpload(event);
              }
            }
          }
        } catch (err) {
          configToast.clear();
          if (err.message !== '用户取消选择') {
            Toast.fail(err.message || '选择文件失败');
            // 提供浏览器上传作为兜底
            await new Promise((r) => setTimeout(r, 1500));
            await this.triggerBrowserUpload(event);
          }
        }
      } catch (err) {
        // 用户取消选择方式（不显示错误）
        console.log('用户取消上传', err);
      }
    },

    // 获取Cookie值的方法
    getCookieValue() {
      console.log('getCookieValue', document.cookie);
      return document.cookie;
    },

    // 修改startUpload方法，在上传完成后更新 vant fileList
    async startUpload(vanFile) {
      // 元数据构建工具函数
      const buildTusMetadata = (file) => {
        const metadata = {
          filename: btoa(encodeURIComponent(file.name)),
          filetype: btoa(encodeURIComponent(file.type)),
        };
        return Object.entries(metadata)
          .map(([k, v]) => `${k} ${v}`)
          .join(',');
      };

      if (!this.file) {
        this.statusMessage = '请先选择一个文件';
        return;
      }

      // ===== before-upload 外部校验 =====
      // 取值优先级：beforeUploadProp > $attrs['before-upload'] > $listeners['before-upload']
      const externalBeforeUpload =
        (this.beforeUploadProp && typeof this.beforeUploadProp === 'function') ? this.beforeUploadProp :
        (this.$attrs && typeof this.$attrs['before-upload'] === 'function') ? this.$attrs['before-upload'] :
        (this.$attrs && typeof this.$attrs.beforeUpload === 'function') ? this.$attrs.beforeUpload :
        (this.$listeners && typeof this.$listeners['before-upload'] === 'function') ? this.$listeners['before-upload'] :
        null;

      if (externalBeforeUpload) {
        try {
          const uploadFile = vanFile?.file || vanFile || this.file;
          const uploadResult = await externalBeforeUpload(uploadFile);
          if (uploadResult === false) {
            throw new Error('before-upload 校验未通过');
          }
        } catch (e) {
          // re-throw 让错误传播到 uploadSingleVanFile 的 catch，
          // 由其从 vanFileList 中移除文件，避免遗留"上传中"状态
          throw e;
        }
      } else {
      }

      // 局部快照，避免异步过程中被其它操作覆盖
      const currentRawFile = this.file;
      // isOpenUpload 配置开关：控制是否显示全局 loading（与 Upload.js 保持一致）
      // 全屏遮罩已开启时跳过：遮罩本身已覆盖全屏，再弹 Toast 会出现两层提示
      const isOpenUpload = this.getConfigItem('isOpenUpload');
      if (isOpenUpload === '1' && !this.blockingLoading) {
        this.uploadLoadingInstance = Toast.loading({
          message: '上传中，请勿离开页面',
          forbidClick: true,
          duration: 0,
        });
      }

      try {
        this.isUploading = true;
        this.isPaused = false;
        this.statusMessage = '正在准备上传...';

        // 1. 创建上传会话 (如果不存在)
        if (!this.uploadUrl) {
          const initResponse = await axios.post(this.tusEndpoint, null, {
            headers: {
              'Tus-Resumable': '1.0.0',
              'Upload-Length': currentRawFile.size,
              'Upload-Metadata': buildTusMetadata(currentRawFile),
              'Expires': this.expiration,
              'SourceSystem': 'wechatWeb',
              'AuthToken': sessionStorage.getItem('AuthToken'),
              'UserId': sessionStorage.getItem('userid'),
            },
          });
          this.uploadUrl = initResponse.headers.location;
        }

        // 2. 获取当前偏移量
        let offset = await this.fetchCurrentOffset();
        this.progress = Math.floor((offset / currentRawFile.size) * 100);
        this.uploadedBytes = offset;

        // 秒传/续传已完成（offset 已达到文件大小）时不再提前 return：
        // 提前 return 会跳过下方统一成功收尾（回填 url、emitData.push、emit change 事件），
        // 导致 vant 预览项无 url 显示为裂图、表单值缺失该文件。直接落入成功收尾分支即可。
        if (offset < currentRawFile.size) {
          Toast.success('开始分块上传...');
        }

        // 3. 分块上传文件
        const chunkSize = 30 * 1024 * 1024; // 30MB

        while (offset < currentRawFile.size && !this.isPaused) {
          const chunk = currentRawFile.slice(offset, offset + chunkSize);
          this.abortController = new AbortController();
          await axios.patch(this.uploadUrl, chunk, {
            headers: {
              'Content-Type': 'application/offset+octet-stream',
              'Upload-Offset': offset,
              'Tus-Resumable': '1.0.0',
              'Expires': this.expiration,
              'SourceSystem': 'wechatWeb',
              'AuthToken': sessionStorage.getItem('AuthToken'),
              'UserId': sessionStorage.getItem('userid'),
            },
            signal: this.abortController.signal,
          });
          // 更新进度
          offset += chunk.size;
          this.uploadedBytes = offset;
          this.progress = Math.floor((offset / currentRawFile.size) * 100);

          // 更新 vant fileList 中文件的状态
          if (vanFile) {
            vanFile.message = `${this.progress}%`;
          }
        }

        if (!this.isPaused) {
          const uploadId = this.uploadUrl?.split('/').pop();
          this.uploadLink = `${this.tusEndpoint}${uploadId}`;
          // 构建与旧组件（Upload.js filePathList）一致的数据格式
          // 注意：不能展开 this.successData（包含不可序列化的 File 对象和 vant 内部字段）
          let addData = {
            name: currentRawFile.name,
            uid: vanFile ? vanFile.uid || Date.now() : Date.now(),
            response: {
              FileList: [
                {
                  name: currentRawFile.name,
                  status: 'success',
                  uid: currentRawFile.uid,
                  url: this.uploadLink,
                },
              ],
            },
          };
          this.emitData.push(addData);
          console.log(this.emitData, 'this.emitData');
          console.log(this.uploadLink, 'this.uploadLink');

          // 更新 vant fileList 中文件的状态和 url
          if (vanFile) {
            const replacedUrl = this.replaceDomain(this.uploadLink);
            console.log('替换后的 URL:', replacedUrl);
            // 使用 $set 确保 Vue 2 能检测到属性变化，触发缩略图重新渲染
            this.$set(vanFile, 'status', 'done');
            this.$set(vanFile, 'message', '');
            this.$set(vanFile, 'url', replacedUrl);
            this.$set(vanFile, '_emitDataUid', addData.uid); // 用于 beforeDelete 匹配
            // 标记为图片，使 vant uploader 在 URL 没有图片后缀时也能正确渲染缩略图
            this.$set(vanFile, 'isImage', this.isImageByName(currentRawFile.name));
            // content（base64）与 url 的选择策略：
            // - 若 _keepLocalPreview（如视频转 GIF 场景）：保留 content，vant 会优先使用 base64 渲染缩略图。
            //   原因：TUS 返回的 uploadLink 是分块上传资源地址，不是可直连的图片 URL，
            //   直接给 <img> 用会裂图。保留本地 base64 可保证上传完成后依然能正常预览。
            // - 其它场景：清空 content，让 vant 切到远端 url（图床直链）。
            if (vanFile.content && !vanFile._keepLocalPreview) {
              this.$set(vanFile, 'content', '');
            }
          }

          // emit 时传出纯净数据（不含内部标记），避免 JSON.stringify 循环引用
          this.$emit('change', this.emitData.map(item => ({
            name: item.name,
            response: item.response,
          })));
          Toast.success(`文件上传成功！ ${this.formatFileSize(currentRawFile.size)} 已上传`);

          // 清除 Formily 错误反馈
          this.setFormilyFeedback();

          // 触发AI审核：逐张即时审核（与 Upload.js 保持一致）
          // 每张图片上传完成后立即对该张图片进行审核，审核失败则移除该图片
          await this.singleImageAiCheck(this.uploadLink, addData, vanFile);
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.message === 'canceled') {
          Toast('上传已暂停');
          return;
        }
        console.error('上传失败:', error);
        Toast.fail(`文件 ${currentRawFile.name} 上传失败`);

        // 更新 vant fileList 中文件状态为失败
        if (vanFile) {
          vanFile.status = 'failed';
          vanFile.message = '上传失败';
          // 同步从 vanFileList 中移除失败占位，避免遗留"上传失败"状态的残留图片。
          // 之前依赖调用方 catch 清理，但本方法 catch 不 re-throw 时调用方 catch 永不执行，
          // 失败项会永久残留；这里直接移除，原生链路与企微链路统一生效。
          const failIdx = this.vanFileList.indexOf(vanFile);
          if (failIdx !== -1) {
            this.vanFileList.splice(failIdx, 1);
          }
        }

        // 设置 Formily 错误反馈
        this.setFormilyFeedback(error);
      } finally {
        // 清除全局loading（与 Upload.js 保持一致）
        Toast.clear();
        if (this.uploadLoadingInstance) {
          this.uploadLoadingInstance.clear();
          this.uploadLoadingInstance = null;
        }
        if (!this.isPaused) {
          this.isUploading = false;
        }
      }
    },

    togglePause() {
      this.isPaused = !this.isPaused;
      if (this.isPaused) {
        if (this.abortController) {
          this.abortController.abort();
        }
        Toast('上传已暂停');
      } else {
        this.startUpload();
      }
    },

    // 取消上传
    cancelUpload() {
      if (this.abortController) {
        this.abortController.abort();
      }

      // 清空待上传队列
      this.uploadQueue = [];

      const uploadId = this.uploadUrl?.split('/').pop();
      if (uploadId) {
        axios.delete(this.tusEndpoint + uploadId).catch(() => {
          console.log('删除上传会话失败，忽略此错误');
        });
      }

      this.file = null;
      this.isUploading = false;
      this.isPaused = false;
      this.progress = 0;
      this.uploadedBytes = 0;
      this.uploadUrl = null;
      Toast('上传已取消');
    },

    async fetchCurrentOffset() {
      const response = await axios.head(this.uploadUrl, {
        headers: {
          'Tus-Resumable': '1.0.0',
          'Expires': this.expiration,
          'SourceSystem': 'wechatWeb',
          'AuthToken': sessionStorage.getItem('AuthToken'),
          'UserId': sessionStorage.getItem('userid'),
        },
      });
      return parseInt(response.headers['upload-offset'], 10);
    },

    formatFileSize(size) {
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let unitIndex = 0;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      return `${size.toFixed(2)} ${units[unitIndex]}`;
    },

    formatProgress(percentage) {
      if (percentage === 100) {
        return `上传完成... (${percentage}%)`;
      } else {
        return `上传中... (${percentage}%)`;
      }
    },
  },
};
</script>

<style scoped lang="less">
.new-upload-wrapper {
  .progress {
    margin-top: 10px;
    margin-bottom: 10px;
    .progress-info {
      margin-top: 10px;
    }
  }
}

/* vant uploader 样式微调 */
/deep/ .van-uploader {
  width: 100%;
}

/deep/ .van-uploader__wrapper {
  display: flex;
  flex-wrap: wrap;
}

/deep/ .van-uploader__preview {
  margin: 0 8px 8px 0;
}

/* AI审核状态样式 */
.ai-check-status {
  margin-top: 10px;
  margin-bottom: 10px;

  .status-indicator {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;

    i {
      margin-right: 6px;
      font-size: 16px;
    }

    &.checking {
      color: #e6a23c;
      background-color: #fdf6ec;
      border: 1px solid #f5dab1;
    }

    &.success {
      color: #67c23a;
      background-color: #f0f9eb;
      border: 1px solid #e1f3d8;
    }

    &.error {
      color: #f56c6c;
      background-color: #fef0f0;
      border: 1px solid #fbc4c4;
    }
  }
}
</style>

<!-- OCR 弹窗样式（非scoped，因弹窗挂载到body上，同时供 Upload.js 共用） -->
<style lang="less">
.ocr-dialog {
  padding: 20px;
  overflow-x: hidden;
}

.ocr-scanning {
  padding-bottom: 10px;
}

.ocr-scanning__header {
  margin-bottom: 20px;
  text-align: center;

  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 500;
    color: #323233;
  }
}

.ocr-scanning__tip {
  margin: 0;
  font-size: 14px;
  color: #969799;
}

.ocr-scanning__image-container {
  position: relative;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.ocr-scanning__image {
  width: 100%;
  height: auto;
  display: block;
}

.ocr-scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #1989fa 50%, transparent 100%);
  box-shadow: 0 0 10px #1989fa, 0 0 20px #1989fa;
  animation: ocrScanning 2s linear infinite;
  z-index: 2;
}

@keyframes ocrScanning {
  0% { top: 0; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { top: 100%; opacity: 0; }
}

.ocr-scan-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(25, 137, 250, 0.1) 0%, transparent 30%, transparent 70%, rgba(25, 137, 250, 0.1) 100%);
  pointer-events: none;
  z-index: 1;
  animation: ocrPulse 2s ease-in-out infinite;
}

@keyframes ocrPulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

.ocr-result {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 70vh;
}

.ocr-result__header {
  flex-shrink: 0;
  padding: 0 12px 6px 0;
  margin-right: 40px;

  h3 {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 600;
    color: #323233;
  }
}

.ocr-result__tip {
  margin: 0;
  font-size: 13px;
  color: #969799;
  line-height: 1.5;
}

.ocr-result__scrollable {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 12px;
}

.ocr-result__image-preview {
  position: relative;
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  max-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
  cursor: pointer;
}

.ocr-result__image {
  max-width: 100%;
  max-height: 200px;
  height: auto;
  display: block;
  object-fit: contain;
}

.ocr-manual-text {
  padding: 0;
}

.ocr-manual-text__content {
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ebedf0;
  min-height: 120px;
  font-size: 14px;
  line-height: 1.8;
  color: #323233;
  white-space: pre-wrap;
  word-break: break-all;
  margin-bottom: 16px;
  user-select: text;
  -webkit-user-select: text;
}

.ocr-manual-hint {
  margin: 0 0 12px 0;
  padding: 12px;
  background: #fff7e6;
  border: 1px solid #ffe7ba;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: #ed6a0c;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  &::before {
    content: "💡";
    font-size: 16px;
    flex-shrink: 0;
  }
}

.ocr-copy-btn {
  width: 100%;
}

.ocr-result__footer {
  flex-shrink: 0;
  padding-top: 12px;
  border-top: 1px solid #ebedf0;
  background: #fff;
  position: sticky;
  bottom: 0;
}

/* 文件预览弹窗样式（移动端全屏） */
.file-preview-popup {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.file-preview-popup__header {
  flex-shrink: 0;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top));
  border-bottom: 1px solid #ebedf0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.file-preview-popup__title {
  font-size: 15px;
  font-weight: 500;
  color: #323233;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 40px);
}

.file-preview-popup__close {
  font-size: 20px;
  color: #969799;
  flex-shrink: 0;
  padding: 4px;
}

.file-preview-popup__body {
  flex: 1;
  overflow: hidden;
  position: relative;
  background: #f7f8fa;
}

.file-preview-popup__loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.file-preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

/* 视频预览：撑满弹窗 body，居中显示 */
.file-preview-video {
  width: 100%;
  height: 100%;
  display: block;
  background: #000;
  object-fit: contain;
}


/* 超时兜底卡片：iframe 无法渲染时叠加在 body 区域 */
.file-preview-popup__fallback-card {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #f7f8fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}

.file-preview-popup__file-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;
}

.file-preview-popup__file-icon {
  margin-bottom: 16px;
}

.file-preview-popup__file-name {
  font-size: 15px;
  color: #323233;
  word-break: break-all;
  margin-bottom: 8px;
  max-width: 280px;
}

.file-preview-popup__file-tip {
  font-size: 13px;
  color: #969799;
  margin-bottom: 24px;
}

/* 底部操作栏：始终显示下载/打开按钮 */
.file-preview-popup__footer {
  flex-shrink: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid #ebedf0;
}

.file-preview-popup__btn {
  flex: 1;
  display: block;
  text-align: center;
  padding: 10px 0;
  font-size: 15px;
  border-radius: 22px;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}

.file-preview-popup__btn--primary {
  background: #1989fa;
  color: #fff;
}

.file-preview-popup__btn--default {
  background: #f7f8fa;
  color: #1989fa;
}



</style> 